/**
 * Typowany klient API. Proxy w Vite wysyła /api na :8000.
 * W produkcji frontend jest serwowany z tego samego procesu co API.
 */

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const isForm = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const headers: HeadersInit = isForm
    ? { ...(init?.headers as HeadersInit) }
    : { "Content-Type": "application/json", ...(init?.headers as HeadersInit) };
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers,
  });
  if (res.status === 401) {
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(formatApiError(res.status, text));
  }
  return res.json() as Promise<T>;
}

/** Nginx 502 HTML nie trafia do UI jako ściana tagów. */
export function formatApiError(status: number, body: string): string {
  const raw = String(body || "").trim();
  const lower = raw.toLowerCase();
  if (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    lower.includes("bad gateway") ||
    lower.includes("<html")
  ) {
    return `${status}: serwer chwilowo niedostępny (restart lub timeout). Spróbuj za kilka sekund.`;
  }
  if (raw.length > 280) {
    return `${status}: ${raw.slice(0, 240)}…`;
  }
  return raw ? `${status}: ${raw}` : `${status}: błąd requestu`;
}

function commentsFilterQuery(params?: CommentsFilterParams): string {
  const sp = new URLSearchParams();
  if (!params) return "";
  if (params.q) sp.set("q", params.q);
  if (params.platform) sp.set("platform", params.platform);
  if (params.window) sp.set("window", params.window);
  if (params.month) sp.set("month", params.month);
  if (params.class) sp.set("class", params.class);
  if (params.pain) sp.set("pain", params.pain);
  if (params.cta != null) sp.set("cta", String(params.cta));
  if (params.is_reply != null) sp.set("is_reply", String(params.is_reply));
  if (params.post_id) sp.set("post_id", params.post_id);
  if (params.action != null) sp.set("action", String(params.action));
  return sp.toString();
}

// --- Auth ---
export const api = {
  login: (password: string) =>
    request<{ ok: boolean }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () =>
    request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),

  bartThreads: () => request<BartThreadsResponse>("/api/bart/threads"),
  bartCreateThread: (title?: string) =>
    request<BartThread>("/api/bart/threads", {
      method: "POST",
      body: JSON.stringify({ title: title || "Nowa rozmowa" }),
    }),
  bartThread: (id: string) => request<BartThreadDetail>(`/api/bart/threads/${id}`),
  bartPinThread: (id: string, pinned: boolean) =>
    request<BartThread>(`/api/bart/threads/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ pinned }),
    }),
  bartDeleteThread: (id: string) =>
    request<{ ok: boolean; id: string }>(`/api/bart/threads/${id}`, { method: "DELETE" }),
  bartUploadImage: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<BartUpload>("/api/bart/uploads", { method: "POST", body });
  },
  bartChat: (message: string, threadId?: string, imageIds?: string[]) =>
    request<BartChatResponse>("/api/bart/chat", {
      method: "POST",
      body: JSON.stringify({
        message,
        thread_id: threadId || null,
        image_ids: imageIds || [],
      }),
    }),
  bartNudge: (threadId: string) =>
    request<BartNudgeResponse>("/api/bart/nudge", {
      method: "POST",
      body: JSON.stringify({ thread_id: threadId }),
    }),
  bartNotes: () => request<BartNotesResponse>("/api/bart/notes"),

  // --- Tasks / Kanban ---
  listTasks: (owner?: string) =>
    request<Task[]>(owner ? `/api/tasks?owner=${encodeURIComponent(owner)}` : "/api/tasks"),
  createTask: (body: Partial<Task> & { links?: TaskLink[]; priority?: TaskPriority }) =>
    request<Task>("/api/tasks", { method: "POST", body: JSON.stringify(body) }),
  updateTask: (
    id: string,
    body: Partial<{
      title: string;
      description: string | null;
      deadline: string | null;
      owner: "arek" | "kasia";
      links: TaskLink[];
      priority: TaskPriority;
      bucket: TaskBucket;
    }>,
  ) => request<Task>(`/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  moveTask: (id: string, bucket: TaskBucket) =>
    request<Task>(`/api/tasks/${id}/move`, {
      method: "PATCH",
      body: JSON.stringify({ bucket }),
    }),
  deleteTask: (id: string) =>
    request<{ ok: boolean }>(`/api/tasks/${id}`, { method: "DELETE" }),

  // --- Status ---
  listStatus: () => request<Subsystem[]>("/api/status"),
  geminiBilling: (days = 30) =>
    request<GeminiBillingSnapshot>(
      `/api/status/gemini-billing?days=${Math.max(1, Math.min(days, 90))}`,
    ),
  setGeminiBalance: (body: { balance_usd: number; note?: string }) =>
    request<GeminiBillingSnapshot>("/api/status/gemini-billing/balance", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // --- Sprzedaż ---
  salesSummary: () => request<SalesSummary>("/api/sales/summary"),
  salesDaily: (days = 30) =>
    request<SalesDailyResponse>(`/api/sales/daily?days=${Math.max(1, Math.min(days, 90))}`),

  // --- Strona i lejek (GA4 + Clarity + Easycart) ---
  siteAnalyticsSummary: (days = 7) =>
    request<SiteAnalyticsSummary>(`/api/site-analytics/summary?days=${days}`),

  /** First-party journey aggregates (tylko agregaty, zero sesji indywidualnych). */
  siteAnalyticsJourney: (params?: SiteAnalyticsJourneyParams) => {
    const sp = new URLSearchParams();
    if (params?.period) {
      sp.set("period", params.period);
    }
    sp.set("days", String(params?.days ?? 7));
    if (params?.entry) sp.set("entry", params.entry);
    if (params?.visitor) sp.set("visitor", params.visitor);
    if (params?.traffic) sp.set("traffic", params.traffic);
    if (params?.version) sp.set("version", params.version);
    if (params?.page_id) sp.set("page_id", params.page_id);
    if (params?.via) sp.set("via", params.via);
    return request<SiteAnalyticsJourney>(`/api/site-analytics/journey?${sp.toString()}`);
  },

  /** Graf drogi klientki: źródło → landing → ścieżka → wyjście/zakup. */
  customerJourneyMap: (params?: CustomerJourneyMapParams) => {
    const sp = new URLSearchParams();
    if (params?.period) sp.set("period", params.period);
    sp.set("days", String(params?.days ?? 7));
    if (params?.entry) sp.set("entry", params.entry);
    if (params?.visitor) sp.set("visitor", params.visitor);
    if (params?.traffic) sp.set("traffic", params.traffic);
    if (params?.version) sp.set("version", params.version);
    if (params?.page_id) sp.set("page_id", params.page_id);
    if (params?.via) sp.set("via", params.via);
    if (params?.source_mode) sp.set("source_mode", params.source_mode);
    return request<CustomerJourneyMapResponse>(
      `/api/site-analytics/customer-journey-map?${sp.toString()}`,
    );
  },

  /** Sesje z koszykiem: lista + wnioski + trendy (bez PII). */
  siteAnalyticsShoppingSessions: (params?: ShoppingSessionsParams) => {
    const sp = new URLSearchParams();
    if (params?.period) sp.set("period", params.period);
    sp.set("days", String(params?.days ?? 7));
    if (params?.outcome) sp.set("outcome", params.outcome);
    sp.set("limit", String(params?.limit ?? 50));
    sp.set("offset", String(params?.offset ?? 0));
    return request<ShoppingSessionsResponse>(
      `/api/site-analytics/shopping-sessions?${sp.toString()}`,
    );
  },

  siteAnalyticsShoppingSessionDetail: (sessionId: string) =>
    request<ShoppingSessionDetailResponse>(
      `/api/site-analytics/shopping-sessions/${encodeURIComponent(sessionId)}`,
    ),

  /** Ruch jednego dnia (today | yesterday) z rozbiciem godzinowym. */
  siteAnalyticsDayTraffic: (period: "today" | "yesterday" = "today") =>
    request<DayTrafficResponse>(
      `/api/site-analytics/day-traffic?period=${encodeURIComponent(period)}`,
    ),

  /** Rolki vs hasła ManyChat vs sprzedaż EasyCart (1/2/3/7 dni, godziny PL). */
  siteAnalyticsContentTimeline: (days: 1 | 2 | 3 | 7 = 7) =>
    request<ContentTimelineResponse>(
      `/api/site-analytics/content-timeline?days=${days}`,
    ),

  /** Katalog landingów do podglądu (/landingi). */
  landingPreviewCatalog: () =>
    request<LandingPreviewCatalogResponse>("/api/landing-preview/catalog"),

  /** Aktualne section/header id z live HTML landingu. */
  landingPreviewStructure: (pageId: string) =>
    request<LandingPreviewStructureResponse>(
      `/api/landing-preview/structure?page_id=${encodeURIComponent(pageId)}`,
    ),

  // --- Rejestr zdarzeń operacyjnych ---
  opsEventTypes: () => request<OpsEventTypesResponse>("/api/ops-events/types"),
  opsEventsList: (params?: { days?: number; from?: string; to?: string; type?: string; source?: string }) => {
    const sp = new URLSearchParams();
    if (params?.days != null) sp.set("days", String(params.days));
    if (params?.from) sp.set("from", params.from);
    if (params?.to) sp.set("to", params.to);
    if (params?.type) sp.set("type", params.type);
    if (params?.source) sp.set("source", params.source);
    const qs = sp.toString();
    return request<OpsEventsListResponse>("/api/ops-events" + (qs ? `?${qs}` : ""));
  },
  opsEventCreate: (body: OpsEventCreateBody) =>
    request<OpsEvent>("/api/ops-events", { method: "POST", body: JSON.stringify(body) }),
  opsEventUpdate: (id: string, body: OpsEventUpdateBody) =>
    request<OpsEvent>(`/api/ops-events/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  opsEventDelete: (id: string) =>
    request<{ ok: boolean }>(`/api/ops-events/${id}`, { method: "DELETE" }),

  // --- Podsumowanie dla Kasi ---
  kasiaSummary: (days: 7 | 14 | 30 = 7) =>
    request<KasiaSummaryResponse>(`/api/summary?days=${days}`),

  // --- Filmy ---
  listVideos: () => request<Video[]>("/api/videos"),

  // --- Komentarze ---
  listComments: (params?: CommentsListParams) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set("q", params.q);
    if (params?.platform) sp.set("platform", params.platform);
    if (params?.window) sp.set("window", params.window);
    if (params?.month) sp.set("month", params.month);
    if (params?.class) sp.set("class", params.class);
    if (params?.pain) sp.set("pain", params.pain);
    if (params?.cta != null) sp.set("cta", String(params.cta));
    if (params?.is_reply != null) sp.set("is_reply", String(params.is_reply));
    if (params?.post_id) sp.set("post_id", params.post_id);
    if (params?.action != null) sp.set("action", String(params.action));
    if (params?.bucket) sp.set("bucket", params.bucket);
    if (params?.limit != null) sp.set("limit", String(params.limit));
    if (params?.offset != null) sp.set("offset", String(params.offset));
    const qs = sp.toString();
    return request<CommentListResponse>("/api/comments" + (qs ? `?${qs}` : ""));
  },
  commentsStats: (params?: CommentsFilterParams) => {
    const sp = commentsFilterQuery(params);
    return request<CommentsStatsResponse>("/api/comments/stats" + (sp ? `?${sp}` : ""));
  },
  commentsFacets: () => request<CommentsFacetsResponse>("/api/comments/facets"),
  commentsPosts: (params?: { limit?: number; window?: string; platform?: string }) => {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set("limit", String(params.limit));
    if (params?.window) sp.set("window", params.window);
    if (params?.platform) sp.set("platform", params.platform);
    const qs = sp.toString();
    return request<CommentsPostsResponse>("/api/comments/posts" + (qs ? `?${qs}` : ""));
  },
  commentsDigest: () => request<CommentsDigestResponse>("/api/comments/digest"),
  commentsFreshness: () => request<CommentsFreshnessResponse>("/api/comments/freshness"),
  commentsCoverage: (recentDays = 30) =>
    request<CommentsCoverageResponse>(`/api/comments/coverage?recent_days=${recentDays}`),
  commentsRefresh: () =>
    request<CommentsRefreshResponse>("/api/comments/refresh", { method: "POST" }),

  // --- Barometr marki ---
  brandBarometer: (window: "7d" | "30d" = "7d") =>
    request<BrandBarometerResponse>(`/api/brand-barometer?window=${window}`),

  // --- Social proof ---
  listSocialProof: (params?: SocialProofListParams) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set("q", params.q);
    if (params?.proof_type) sp.set("proof_type", params.proof_type);
    if (params?.status) sp.set("status", params.status);
    if (params?.source_kind) sp.set("source_kind", params.source_kind);
    if (params?.limit != null) sp.set("limit", String(params.limit));
    if (params?.offset != null) sp.set("offset", String(params.offset));
    const qs = sp.toString();
    return request<SocialProofListResponse>("/api/social-proof" + (qs ? `?${qs}` : ""));
  },
  socialProofFacets: () => request<SocialProofFacets>("/api/social-proof/facets"),
  socialProofFreshness: () => request<SocialProofFreshness>("/api/social-proof/freshness"),
  socialProofRescan: () =>
    request<{ status: string; added: number; updated: number; total: number }>(
      "/api/social-proof/rescan",
      { method: "POST" },
    ),
  patchSocialProof: (id: string, body: SocialProofPatchBody) =>
    request<{ ok: boolean; item: SocialProofItem }>(
      `/api/social-proof/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  socialProofExportApproved: () =>
    request<SocialProofExportResponse>("/api/social-proof/export/approved"),

  // --- Historie Messenger ---
  messengerStories: (params?: MessengerStoriesParams) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set("q", params.q);
    if (params?.class) sp.set("class", params.class);
    if (params?.theme) sp.set("theme", params.theme);
    if (params?.persona) sp.set("persona", params.persona);
    if (params?.b_signal) sp.set("b_signal", params.b_signal);
    if (params?.month) sp.set("month", params.month);
    if (params?.length_band) sp.set("length_band", params.length_band);
    if (params?.has_purchase != null) sp.set("has_purchase", String(params.has_purchase));
    if (params?.limit != null) sp.set("limit", String(params.limit));
    if (params?.offset != null) sp.set("offset", String(params.offset));
    const qs = sp.toString();
    return request<MessengerStoryListResponse>(
      "/api/messenger-stories" + (qs ? `?${qs}` : ""),
    );
  },
  messengerStoriesFacets: () => request<MessengerStoriesFacets>("/api/messenger-stories/facets"),
  messengerStory: (storyId: string) =>
    request<MessengerStory>(`/api/messenger-stories/${encodeURIComponent(storyId)}`),
  messengerParticipantProfile: (participantId: string) =>
    request<MessengerParticipantProfile>(
      `/api/messenger-stories/participants/${encodeURIComponent(participantId)}`,
    ),

  // --- Zespół AI ---
  listAgents: () => request<AgentRole[]>("/api/discussions/agents"),
  naradaCatalog: () => request<NaradaCatalog>("/api/discussions/catalog"),
  listDiscussions: () => request<Discussion[]>("/api/discussions"),
  getDiscussion: (id: number) => request<DiscussionDetail>(`/api/discussions/${id}`),
  startNarada: (body: { schema_id: string; area_ids: string[]; question: string }) =>
    request<DiscussionDetail>("/api/discussions", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // --- Wysyłka / fulfillment ---
  fulfillmentSummary: () => request<FulfillmentSummary>("/api/fulfillment/summary"),
  fulfillmentOrders: (params?: {
    status?: string;
    q?: string;
    has_dedication?: boolean;
    attention?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set("status", params.status);
    if (params?.q) sp.set("q", params.q);
    if (params?.has_dedication != null) sp.set("has_dedication", String(params.has_dedication));
    if (params?.attention) sp.set("attention", "true");
    if (params?.limit != null) sp.set("limit", String(params.limit));
    if (params?.offset != null) sp.set("offset", String(params.offset));
    const qs = sp.toString();
    return request<FulfillmentListResponse>("/api/fulfillment/orders" + (qs ? `?${qs}` : ""));
  },
  fulfillmentOrder: (id: number) => request<FulfillmentOrderDetail>(`/api/fulfillment/orders/${id}`),
  fulfillmentPatch: (id: number, patch: FulfillmentOrderPatch) =>
    request<FulfillmentOrderDetail>(`/api/fulfillment/orders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  fulfillmentAdditionalShipment: (
    id: number,
    body?: FulfillmentAdditionalShipmentRequest,
  ) =>
    request<FulfillmentAdditionalShipmentResult>(
      `/api/fulfillment/orders/${id}/additional-shipment`,
      {
        method: "POST",
        body: JSON.stringify(body ?? {}),
      },
    ),
  fulfillmentResolvePaczkomat: (
    id: number,
    body?: PaczkomatResolveRequest,
  ) =>
    request<PaczkomatResolveResponse>(`/api/fulfillment/orders/${id}/paczkomat/resolve`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),
  fulfillmentImportStatus: () =>
    request<FulfillmentImportStatus>("/api/fulfillment/import/status"),
  fulfillmentSync: () =>
    request<FulfillmentImportResult>("/api/fulfillment/import/sync", {
      method: "POST",
    }),
  fulfillmentUploadCsv: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<FulfillmentImportResult>("/api/fulfillment/import/csv", {
      method: "POST",
      body: form,
    });
  },
  fulfillmentExportCsvUrl: (ids: number[]) =>
    `/api/fulfillment/export/furgonetka.csv?ids=${ids.join(",")}`,
  furgonetkaConfig: () => request<FurgonetkaConfig>("/api/fulfillment/furgonetka/config"),
  furgonetkaServices: (shipmentMode?: string) => {
    const qs = shipmentMode ? `?shipment_mode=${encodeURIComponent(shipmentMode)}` : "";
    return request<FurgonetkaServicesResponse>(`/api/fulfillment/furgonetka/services${qs}`);
  },
  furgonetkaSetSandbox: (sandbox: boolean) =>
    request<FurgonetkaConfig>("/api/fulfillment/furgonetka/config", {
      method: "PATCH",
      body: JSON.stringify({ sandbox_mode: sandbox }),
    }),
  furgonetkaSetCourier: (body: { service_id: number; service: string; label?: string }) =>
    request<FurgonetkaConfig>("/api/fulfillment/furgonetka/config", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  furgonetkaSetShipmentMode: (shipment_mode: string) =>
    request<FurgonetkaConfig>("/api/fulfillment/furgonetka/config", {
      method: "PATCH",
      body: JSON.stringify({ shipment_mode }),
    }),
  furgonetkaSetParcelSettings: (body: Partial<FurgonetkaParcelSettings>) =>
    request<FurgonetkaConfig>("/api/fulfillment/furgonetka/config", {
      method: "PATCH",
      body: JSON.stringify({
        parcel_type: body.parcel_type,
        parcel_length_cm: body.length_cm,
        parcel_width_cm: body.width_cm,
        parcel_height_cm: body.height_cm,
        parcel_weight_kg: body.weight_kg,
        parcel_value_pln: body.value_pln,
        parcel_content: body.content,
      }),
    }),
  furgonetkaSyncCandidates: () =>
    request<{ ids: number[]; total: number }>("/api/fulfillment/orders/furgonetka/sync-candidates"),
  furgonetkaSyncStatus: (ids?: number[]) =>
    request<FurgonetkaSyncStatusResult>("/api/fulfillment/orders/furgonetka/sync-status", {
      method: "POST",
      body: JSON.stringify({ ids: ids ?? [] }),
    }),
  furgonetkaCreateBatch: (body: {
    ids: number[];
    service?: string;
    service_id?: number;
    shipment_mode?: string;
    confirm?: boolean;
    quote_only?: boolean;
  }) =>
    request<FurgonetkaBatchResult>("/api/fulfillment/orders/furgonetka/create-batch", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  furgonetkaLabelUrl: (orderId: number) =>
    `/api/fulfillment/orders/${orderId}/furgonetka/label`,
  async furgonetkaLabelBlob(orderId: number): Promise<Blob> {
    const res = await fetch(api.furgonetkaLabelUrl(orderId), { credentials: "include" });
    if (!res.ok) {
      let detail = "Nie udało się pobrać etykiety";
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    return res.blob();
  },

  carouselCatalog: () => request<CarouselCatalog>("/api/carousels/catalog"),
  carouselSources: (params?: {
    pain_id?: string;
    book_id?: string;
    tool_id?: string;
    use_book?: boolean;
    use_comments?: boolean;
    use_messenger?: boolean;
    use_stories?: boolean;
  }) => {
    const sp = new URLSearchParams();
    if (params?.pain_id) sp.set("pain_id", params.pain_id);
    if (params?.book_id) sp.set("book_id", params.book_id);
    if (params?.tool_id) sp.set("tool_id", params.tool_id);
    if (params?.use_book === false) sp.set("use_book", "false");
    if (params?.use_comments === false) sp.set("use_comments", "false");
    if (params?.use_messenger === false) sp.set("use_messenger", "false");
    if (params?.use_stories === false) sp.set("use_stories", "false");
    const qs = sp.toString();
    return request<CarouselSourcesResponse>("/api/carousels/sources" + (qs ? `?${qs}` : ""));
  },
  carouselStatus: () => request<CarouselStudioStatus>("/api/carousels/status"),
  carouselMusicStatus: () => request<EpidemicStatus>("/api/carousels/music/status"),
  carouselMusicSearch: (q: string, limit = 8) =>
    request<{ term: string; tracks: EpidemicTrack[] }>(
      `/api/carousels/music/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),
  carouselMusicPropose: (id: string) =>
    request<{ term: string; tracks: EpidemicTrack[] }>(`/api/carousels/${id}/music/propose`),
  carouselMusicPreview: (trackId: string) => `/api/carousels/music/${encodeURIComponent(trackId)}/preview`,
  carouselComposeReel: (id: string, trackId: string) =>
    request<CarouselItem>(`/api/carousels/${id}/reel`, {
      method: "POST",
      body: JSON.stringify({ track_id: trackId }),
    }),
  carouselReelUrl: (id: string) => `/api/carousels/${id}/reel`,
  carouselList: (params?: { status?: string; week?: string }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set("status", params.status);
    if (params?.week) sp.set("week", params.week);
    const qs = sp.toString();
    return request<CarouselListResponse>("/api/carousels" + (qs ? `?${qs}` : ""));
  },
  carouselGet: (id: string) => request<CarouselItem>(`/api/carousels/${id}`),
  carouselDelete: (id: string) =>
    request<{ ok: boolean; id: string; short_id?: string }>(`/api/carousels/${id}`, {
      method: "DELETE",
    }),
  carouselCreate: (body: CarouselBrief) =>
    request<CarouselCreateResponse>("/api/carousels", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselSelectIdea: (id: string, ideaId: string) =>
    request<CarouselItem>(`/api/carousels/${id}/select/${ideaId}`, { method: "POST" }),
  carouselGenerateCopy: (id: string, feedback = "") =>
    request<CarouselItem>(`/api/carousels/${id}/generate-copy`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),
  carouselGenerateCopyRevise: (id: string, feedback = "") =>
    request<CarouselItem>(`/api/carousels/${id}/generate-copy-revise`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),
  carouselCopyChat: (id: string, message: string) =>
    request<CarouselItem>(`/api/carousels/${id}/copy-chat`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),
  carouselCopyReview: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/copy-review`, { method: "POST" }),
  carouselCopyChatUndo: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/copy-chat/undo`, { method: "POST" }),
  carouselApplyCopyReview: (id: string, actionIds: string[], rejectIds?: string[] | null) =>
    request<CarouselItem>(`/api/carousels/${id}/apply-copy-review`, {
      method: "POST",
      body: JSON.stringify({
        action_ids: actionIds,
        ...(rejectIds === undefined ? {} : { reject_ids: rejectIds }),
      }),
    }),
  carouselPatchCopy: (id: string, body: { copy?: Record<string, unknown>; caption?: string; caption_engage?: string }) =>
    request<CarouselItem>(`/api/carousels/${id}/copy`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  czasotekstCopy: (id: string, feedback = "") =>
    request<CarouselItem>(`/api/carousels/${id}/czasotekst/copy`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),
  czasotekstChat: (id: string, message: string, apply = false) =>
    request<CarouselItem>(`/api/carousels/${id}/czasotekst/chat`, {
      method: "POST",
      body: JSON.stringify({ message, apply }),
    }),
  czasotekstCompose: (
    id: string,
    body: {
      asset_id?: string;
      in_sec?: number;
      out_sec?: number;
      scale?: number;
      x?: number;
      y?: number;
      loop_sec?: number;
      hook_on_screen?: string;
      overlay?: CzasotekstOverlay;
      bed_id?: string;
      audio_crossfade_sec?: number;
      compose?: boolean;
    },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/czasotekst/compose`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselPatchCta: (
    id: string,
    body: {
      cta_intent?: string;
      close_mode?: string;
      ab_variable?: string;
      ab_pair_id?: string;
    },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/cta`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  carouselPatchTemplate: (
    id: string,
    body: {
      style_id?: string;
      visual_pack_id?: string;
      format_id?: string;
      bg_unique_count?: number;
      bg_mode?: "flat" | "gemini";
      bg_hint?: string;
      bg_preset_id?: string;
      bg_placement_id?: string;
      layout_preset_id?: string;
      style_apply_mode?: "bg" | "full" | "layout";
      czasotekst_form_id?: string;
      czasotekst_variant_id?: string;
    },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/template`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  carouselApproveCopy: (id: string, fallback = false) =>
    request<CarouselItem>(`/api/carousels/${id}/copy/approve?fallback=${fallback}`, { method: "POST" }),
  carouselRender: (id: string, fallback = false) =>
    request<CarouselItem>(`/api/carousels/${id}/render?fallback=${fallback}`, { method: "POST" }),
  carouselCancelLlm: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/llm/cancel`, { method: "POST" }),
  carouselCancelRender: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/render/cancel`, { method: "POST" }),
  carouselRerenderSlide: (id: string, index: number, fallback = false, apply: "one" | "all" = "one") =>
    request<{ ok: boolean }>(
      `/api/carousels/${id}/slides/${index}/rerender?fallback=${fallback}&apply=${apply}`,
      {
        method: "POST",
      },
    ),
  carouselPutSlideLayout: (
    id: string,
    index: number,
    layout: CarouselSlideLayout,
    apply: "one" | "all" = "one",
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/layout`, {
      method: "PUT",
      body: JSON.stringify({ layout, apply }),
    }),
  carouselComposeSlide: (id: string, index: number) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/compose`, { method: "POST" }),
  carouselSyncSlideCopy: (id: string, index: number) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/sync-copy`, { method: "POST" }),
  carouselBake: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/bake`, { method: "POST" }),
  carouselRecompose: (id: string, syncFromCopy = false) =>
    request<CarouselItem>(`/api/carousels/${id}/recompose?sync_from_copy=${syncFromCopy}`, { method: "POST" }),
  carouselLayoutQa: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/layout/qa`, { method: "POST" }),
  carouselDesignReview: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/design/review`, { method: "POST" }),
  carouselDesignApply: (id: string, selected?: number[]) =>
    request<CarouselItem>(`/api/carousels/${id}/design/apply`, {
      method: "POST",
      body: JSON.stringify({ selected }),
    }),
  carouselDesignFeedback: (id: string, body: { rating?: string; text?: string }) =>
    request<CarouselItem>(`/api/carousels/${id}/design/feedback`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselDesignPng: (id: string, side: "before" | "after", index: number, passN = 0) =>
    `/api/carousels/${id}/design/${side}/${index}?pass_n=${passN}`,
  carouselHouseFix: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/design/fix`, { method: "POST" }),
  carouselDesignLoop: (id: string) =>
    request<{ id: string; loop?: CarouselComposeLoop; design_review?: CarouselDesignReview }>(
      `/api/carousels/${id}/design/loop`,
      { method: "POST" },
    ),
  carouselDesignLoopPng: (id: string, turn: number, side: "now" | "after", index: number) =>
    `/api/carousels/${id}/design/loop/${turn}/${side}/${index}`,
  carouselResetSlideLayout: (id: string, index: number) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/layout/reset`, { method: "POST" }),
  carouselPropagateLayout: (
    id: string,
    index: number,
    body: {
      box_id: string;
      mode: "apply" | "add";
      layout?: CarouselSlideLayout;
      params?: Array<"font" | "color" | "size" | "position">;
    },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/layout/propagate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselLayoutPlan: (id: string, draftText = "", draftEditMode = "") =>
    request<CarouselItem>(`/api/carousels/${id}/layout-plan`, {
      method: "POST",
      body: JSON.stringify({ draft_text: draftText, draft_edit_mode: draftEditMode }),
    }),
  carouselFromDraft: (id: string, draftText = "", draftEditMode = "") =>
    request<CarouselItem>(`/api/carousels/${id}/from-draft`, {
      method: "POST",
      body: JSON.stringify({ draft_text: draftText, draft_edit_mode: draftEditMode }),
    }),
  carouselWriteCaption: (id: string, feedback = "", verifyCta = true) =>
    request<CarouselItem>(`/api/carousels/${id}/caption`, {
      method: "POST",
      body: JSON.stringify({ feedback, verify_cta: verifyCta }),
    }),
  carouselCaptionChatUndo: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/caption/undo`, { method: "POST" }),
  carouselComposeMode: (id: string, composeMode: "operator" | "ai") =>
    request<CarouselItem>(`/api/carousels/${id}/compose-mode`, {
      method: "PATCH",
      body: JSON.stringify({ compose_mode: composeMode }),
    }),
  carouselPatchBulletIcon: (id: string, bulletIcon: string) =>
    request<CarouselItem>(`/api/carousels/${id}/bullet-icon`, {
      method: "PATCH",
      body: JSON.stringify({ bullet_icon: bulletIcon }),
    }),
  carouselChanges: (status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<{ items: CarouselChangeProposal[] }>(`/api/carousels/changes${qs}`);
  },
  carouselChangesFromRetros: (ids: string[] = [], limit = 5) =>
    request<{ items: CarouselChangeProposal[]; count: number }>("/api/carousels/changes/from-retros", {
      method: "POST",
      body: JSON.stringify({ ids, limit }),
    }),
  carouselChangeApprove: (id: string) =>
    request<CarouselChangeProposal>(`/api/carousels/changes/${id}/approve`, { method: "POST" }),
  carouselChangeReject: (id: string) =>
    request<CarouselChangeProposal>(`/api/carousels/changes/${id}/reject`, { method: "POST" }),
  carouselUpdateSlideBackground: (
    id: string,
    index: number,
    opts?: { file?: File; fallback?: boolean; apply?: "one" | "all"; forceGemini?: boolean; hint?: string },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.forceGemini) qs.set("force_gemini", "1");
    else if (opts?.fallback) qs.set("fallback", "true");
    qs.set("apply", opts?.apply || "one");
    const form = new FormData();
    if (opts?.file) form.append("file", opts.file);
    if (opts?.hint != null) form.append("hint", opts.hint);
    return request<CarouselItem>(`/api/carousels/${id}/slides/${index}/background?${qs}`, {
      method: "POST",
      body: form,
    });
  },
  carouselBackgroundCandidates: (
    id: string,
    index: number,
    body?: { count?: number; hint?: string | null },
  ) =>
    request<{
      ok: boolean;
      carousel_id: string;
      index_n: number;
      count: number;
      candidates: { k: number; relpath?: string; url: string }[];
      errors?: string[];
      progress?: CarouselBgCandProgress | null;
    }>(`/api/carousels/${id}/slides/${index}/background/candidates`, {
      method: "POST",
      body: JSON.stringify({ count: body?.count ?? 3, hint: body?.hint ?? null }),
    }),
  carouselBackgroundCandidateApply: (
    id: string,
    index: number,
    body: { candidate_k: number; apply?: "one" | "all" },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/background/candidates/apply`, {
      method: "POST",
      body: JSON.stringify({ candidate_k: body.candidate_k, apply: body.apply || "one" }),
    }),
  carouselBackgroundCandidateUrl: (id: string, index: number, k: number) =>
    `/api/carousels/${id}/slides/${index}/background/candidates/${k}`,
  carouselSlideBackground: (id: string, index: number) =>
    `/api/carousels/${id}/slides/${index}/background`,
  carouselApprove: (id: string) =>
    request<CarouselItem>(`/api/carousels/${id}/approve`, { method: "POST" }),
  carouselSchedule: (
    id: string,
    body: {
      scheduled_at: string;
      publish_now?: boolean;
      channel?: CarouselPublishChannel;
      fb_format?: CarouselFbPublishMode;
      track_id?: string;
      force?: boolean;
    },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/schedule`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselRepublish: (
    id: string,
    body: {
      channel: CarouselPublishChannel;
      fb_format: CarouselFbPublishMode;
      track_id?: string;
      publish_now: true;
    },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/republish`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselRetry: (
    id: string,
    body?: { channel?: CarouselPublishChannel; fb_format?: CarouselFbPublishMode; track_id?: string },
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/retry`, {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
  carouselFromPublished: (id: string) =>
    request<CarouselCreateResponse>(`/api/carousels/${id}/from-published`, { method: "POST" }),
  carouselPause: (paused: boolean) =>
    request<{ ok: boolean; queue_paused: boolean }>("/api/carousels/pause", {
      method: "POST",
      body: JSON.stringify({ paused }),
    }),
  carouselRanking: () => request<CarouselRankingResponse>("/api/carousels/ranking"),
  carouselPublishSlot: (excludeId?: string) => {
    const qs = excludeId ? `?exclude_id=${encodeURIComponent(excludeId)}` : "";
    return request<CarouselPublishSlot>(`/api/carousels/publish-slot${qs}`);
  },
  carouselBatchWeek: (week: string) =>
    request<{ count: number }>(`/api/carousels/batch-week/${week}`, { method: "POST" }),
  carouselUploadInspiration: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<CarouselItem>(`/api/carousels/${id}/inspiration/upload`, {
      method: "POST",
      body: form,
    });
  },
  carouselInspirationFile: (id: string, filename: string) =>
    `/api/carousels/${id}/inspiration/${encodeURIComponent(filename)}`,
  carouselInspirationBorrow: (id: string, body: CarouselInspirationBorrow) =>
    request<CarouselItem>(`/api/carousels/${id}/inspiration/borrow`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  carouselInspirationAnalyze: (id: string, body?: { regenerate?: boolean; force_fetch?: boolean }) =>
    request<CarouselItem>(`/api/carousels/${id}/inspiration/analyze`, {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
  carouselSlidePng: (id: string, index: number) =>
    `/api/carousels/${id}/slides/${index}/png`,
  carouselPrompts: () => request<CarouselPromptsResponse>("/api/carousels/settings/prompts"),
  carouselPromptsPut: (values: Record<string, string>) =>
    request<CarouselPromptsResponse>("/api/carousels/settings/prompts", {
      method: "PUT",
      body: JSON.stringify({ values }),
    }),
  carouselPromptsRestore: (keys?: string[]) =>
    request<CarouselPromptsResponse>("/api/carousels/settings/prompts/restore", {
      method: "POST",
      body: JSON.stringify(keys?.length ? { keys } : {}),
    }),
  carouselLessons: (status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<{ items: CarouselLesson[] }>(`/api/carousels/lessons${qs}`);
  },
  carouselLessonPatch: (id: string, body: { status?: string; kind?: string; text?: string }) =>
    request<CarouselLesson>(`/api/carousels/lessons/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  carouselLessonCreate: (body: { text: string; kind?: string; carousel_id?: string }) =>
    request<CarouselLesson>("/api/carousels/lessons", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselRating: (id: string, rating: string) =>
    request<CarouselItem>(`/api/carousels/${id}/rating`, {
      method: "PATCH",
      body: JSON.stringify({ rating }),
    }),
  carouselRetro: (id: string, phase: "discussion" | "results" = "discussion") =>
    request<CarouselItem>(`/api/carousels/${id}/retro?phase=${encodeURIComponent(phase)}`, { method: "POST" }),
  carouselAttachMedia: (
    id: string,
    index: number,
    body: { asset_id: string; mode: string },
    fallback = false,
    apply: "one" | "all" = "one",
  ) =>
    request<CarouselItem>(`/api/carousels/${id}/slides/${index}/media?fallback=${fallback}&apply=${apply}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  carouselSlideMp4: (id: string, index: number) => `/api/carousels/${id}/slides/${index}/mp4`,
  mediaReelsPublished: (params?: { limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    return request<{ items: PublishedReel[]; error?: string }>(
      "/api/media/reels/published" + (qs.toString() ? `?${qs}` : ""),
    );
  },
  mediaReelImport: (mediaId: string) =>
    request<{ asset: MediaAsset; created: boolean; reason?: string }>("/api/media/reels/import", {
      method: "POST",
      body: JSON.stringify({ media_id: mediaId }),
    }),
  mediaClip: (
    assetId: string,
    body: {
      in_sec: number;
      out_sec: number;
      mode: "still" | "video";
      carousel_id: string;
      index_n: number;
      apply?: "one" | "all";
      fallback?: boolean;
      scale?: number;
      x?: number;
      y?: number;
    },
  ) => request<CarouselItem>(`/api/media/${assetId}/clip`, { method: "POST", body: JSON.stringify(body) }),
  mediaList: (params?: {
    status?: string;
    person?: string;
    funnel_fit?: string;
    usable_as?: string;
    origin?: string;
    starred?: boolean;
    q?: string;
    kind?: string;
    exclude_origin?: string;
    exclude_status?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (k === "starred" && typeof v === "boolean") {
        qs.set(k, v ? "1" : "0");
        return;
      }
      if (v) qs.set(k, String(v));
    });
    return request<MediaListResponse>("/api/media" + (qs.toString() ? `?${qs}` : ""));
  },
  mediaScan: () => request<MediaScanResponse>("/api/media/scan", { method: "POST" }),
  mediaUpload: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<{ asset: MediaAsset; created: boolean }>("/api/media/upload", { method: "POST", body: fd });
  },
  mediaSuggest: (body: MediaSuggestBody) =>
    request<{ items: MediaAsset[] }>("/api/media/suggest", { method: "POST", body: JSON.stringify(body) }),
  mediaPatch: (id: string, body: Partial<MediaAsset> & { tags?: string[] }) =>
    request<MediaAsset>(`/api/media/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  mediaReady: (id: string) => request<MediaAsset>(`/api/media/${id}/ready`, { method: "POST" }),
  mediaRetag: (id: string) => request<MediaAsset>(`/api/media/${id}/retag`, { method: "POST" }),
  mediaDelete: (id: string) => request<{ ok: boolean }>(`/api/media/${id}`, { method: "DELETE" }),
  mediaFile: (id: string, thumb = true) => `/api/media/${id}/file?thumb=${thumb ? "true" : "false"}`,
  fontsList: (params?: { q?: string; category?: string; starred?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.category) qs.set("category", params.category);
    if (params?.starred === true) qs.set("starred", "1");
    if (params?.starred === false) qs.set("starred", "0");
    return request<FontLibraryResponse>("/api/fonts" + (qs.toString() ? `?${qs}` : ""));
  },
  fontPatch: (id: string, starred: boolean) =>
    request<FontFamily>(`/api/fonts/${id}`, { method: "PATCH", body: JSON.stringify({ starred }) }),
  fontsSync: () => request<FontSyncResponse>("/api/fonts/sync", { method: "POST" }),
  weekPlan: (week?: string) => request<WeekPlan>(week ? `/api/content/week/${week}` : "/api/content/week"),
  weekPlanSave: (week: string, slots: WeekSlot[], meta?: WeekPlanMeta) =>
    request<WeekPlan>(`/api/content/week/${week}/slots`, {
      method: "PUT",
      body: JSON.stringify({ slots, meta }),
    }),
  weekPlanSaveMeta: (week: string, meta: WeekPlanMeta) =>
    request<WeekPlan>(`/api/content/week/${week}/meta`, {
      method: "PUT",
      body: JSON.stringify({ meta }),
    }),
  weekPlanApprove: (week: string, generate = true) =>
    request<WeekPlan>(`/api/content/week/${week}/approve?generate=${generate}`, { method: "POST" }),
  weekPlanGenerate: (week: string) =>
    request<{ ok: boolean; created: ContentItem[]; plan: WeekPlan }>(`/api/content/week/${week}/generate`, {
      method: "POST",
    }),
  weekPlanAskTeam: (week: string) =>
    request<{ ok: boolean; discussion_id: number; question: string; href: string }>(
      `/api/content/week/${week}/ask-team`,
      { method: "POST" },
    ),
  arekWeekTemplate: () => request<ArekWeekTemplate>("/api/arek-week/template"),
  arekWeekTemplateSave: (
    body: ArekWeekTemplate & { merge_into_week?: string },
  ) =>
    request<{ template: ArekWeekTemplate; week: ArekWeekInstance | null }>("/api/arek-week/template", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  arekWeekTemplateReset: () =>
    request<ArekWeekTemplate>("/api/arek-week/template/reset", { method: "POST" }),
  arekWeek: (week: string) => request<ArekWeekInstance>(`/api/arek-week/${week}`),
  arekWeekSave: (
    week: string,
    body: { header?: ArekWeekHeader; days?: ArekWeekDay[]; footer?: ArekWeekFooter },
  ) =>
    request<ArekWeekInstance>(`/api/arek-week/${week}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  arekWeekCheck: (week: string, item_id: string, done: boolean) =>
    request<ArekWeekInstance>(`/api/arek-week/${week}/check`, {
      method: "PATCH",
      body: JSON.stringify({ item_id, done }),
    }),
  contentItems: (params?: { week?: string; kind?: string; owner?: string }) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v) qs.set(k, String(v));
    });
    return request<{ items: ContentItem[] }>("/api/content/items" + (qs.toString() ? `?${qs}` : ""));
  },
  contentItemPatch: (id: string, body: { status?: string; filmed?: boolean; brief_text?: string; funnel_stage?: string }) =>
    request<ContentItem>(`/api/content/items/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  publicationsHub: (params?: {
    days?: 7 | 14 | 30;
    week?: string;
    kind?: string;
    funnel_stage?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.days) qs.set("days", String(params.days));
    if (params?.week) qs.set("week", params.week);
    if (params?.kind) qs.set("kind", params.kind);
    if (params?.funnel_stage) qs.set("funnel_stage", params.funnel_stage);
    return request<PublicationsHub>(
      "/api/content/publications-hub" + (qs.toString() ? `?${qs}` : ""),
    );
  },
  publicationsHubSync: () =>
    request<{ status: string; task_id?: string | null; job?: string }>(
      "/api/content/publications-hub/sync",
      { method: "POST" },
    ),

  wiedzaCatalog: () => request<WiedzaCatalogResponse>("/api/wiedza/catalog"),
  wiedzaItem: (id: string) => request<WiedzaItemDetail>(`/api/wiedza/items/${encodeURIComponent(id)}`),
  wiedzaRawUrl: (id: string) => `/api/wiedza/raw/${encodeURIComponent(id)}`,
  wiedzaPrefs: () => request<WiedzaPrefs>("/api/wiedza/prefs"),
  wiedzaStar: (id: string, starred: boolean) =>
    request<{ item_id: string; starred: boolean }>(`/api/wiedza/items/${encodeURIComponent(id)}/star`, {
      method: "PATCH",
      body: JSON.stringify({ starred }),
    }),
  wiedzaProgress: (
    id: string,
    body: { scroll_pct: number; scroll_anchor?: string | null; completed?: boolean },
  ) =>
    request<WiedzaProgressRow>(`/api/wiedza/items/${encodeURIComponent(id)}/progress`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  wiedzaLast: (item_id: string) =>
    request<{ last_item_id: string }>("/api/wiedza/last", {
      method: "PUT",
      body: JSON.stringify({ item_id }),
    }),

  knowledgeFlowGraph: () =>
    request<KnowledgeFlowGraph>("/api/knowledge-flow/graph"),
};

// --- Typy ---
export type TaskBucket = "todo" | "in_progress" | "done";
export type TaskPriority = "P0" | "P1" | "P2";
export interface TaskLink {
  url: string;
  label?: string | null;
}
export interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: string;
  status: string;
  bucket: TaskBucket;
  agent: string | null;
  deadline: string | null;
  source_discussion: string | null;
  owner?: "arek" | "kasia" | null;
  links?: TaskLink[];
  priority?: TaskPriority | string;
}

export interface MediaAsset {
  id: string;
  sha256?: string;
  original_name?: string;
  status: string;
  usable_as?: string;
  person?: string;
  mood?: string;
  place?: string;
  colors?: string;
  funnel_fit?: string;
  note?: string;
  origin?: string;
  starred?: boolean;
  tags?: string[];
  score?: number;
  width?: number;
  height?: number;
  kind?: string;
  duration_sec?: number;
  mime?: string;
  external_id?: string;
}

export interface FontFamily {
  id: string;
  name: string;
  category: string;
  license?: string;
  files?: Record<string, string>;
  available?: boolean;
  starred?: boolean;
  regular_url?: string;
  bold_url?: string;
}

export interface FontLibraryResponse {
  items: FontFamily[];
  count: number;
  available?: number;
}

export interface FontSyncResponse extends FontLibraryResponse {
  ok?: boolean;
  downloaded?: number;
  skipped?: number;
  missing?: string[];
  errors?: string[];
  total?: number;
}

export interface WiedzaCategory {
  id: string;
  label: string;
}

export interface WiedzaTrack {
  id: string;
  label: string;
  summary: string;
  tags: string[];
}

export interface WiedzaItem {
  id: string;
  title: string;
  summary: string;
  category: string;
  tags: string[];
  path: string;
  format: "md" | "html" | "pdf" | string;
  featured?: boolean;
  parentId?: string | null;
  order?: number | null;
  moduleHub?: boolean;
  minutes?: number | null;
  available: boolean;
  sourcePath?: string | null;
  starred?: boolean;
  scroll_pct?: number;
  last_read_at?: string | null;
  completed?: boolean;
  scroll_anchor?: string | null;
}

export interface WiedzaProgressRow {
  item_id?: string;
  scroll_pct: number;
  scroll_anchor?: string | null;
  last_read_at?: string | null;
  completed?: boolean;
}

export interface WiedzaPrefs {
  stars: Record<string, boolean>;
  progress: Record<string, WiedzaProgressRow>;
  last_item_id: string | null;
}

export interface WiedzaReadingGuideStep {
  itemId: string;
  why?: string;
}

export interface WiedzaReadingGuidePhase {
  id: string;
  title: string;
  summary?: string;
  description?: string;
  steps: WiedzaReadingGuideStep[];
}

export interface WiedzaReadingGuide {
  title: string;
  subtitle?: string;
  phases: WiedzaReadingGuidePhase[];
}

export interface WiedzaCatalogResponse {
  version: number;
  categories: WiedzaCategory[];
  tracks: WiedzaTrack[];
  items: WiedzaItem[];
  count: number;
  available_count: number;
  last_item_id?: string | null;
  readingGuide?: WiedzaReadingGuide | null;
}

export interface WiedzaItemDetail {
  item: WiedzaItem;
  body: string | null;
  raw_url: string;
  mime: string;
  bytes: number;
}

export type KnowledgeFlowWindow = 7 | 14 | 28;

export interface KnowledgeFlowStats {
  d7: number;
  d14: number;
  d28: number;
}

export interface KnowledgeFlowNode {
  id: string;
  kind: "ai" | "source";
  subtype: string;
  label: string;
  description: string;
  paths: string[];
  href?: string | null;
  recommend_access?: string | null;
  position: { x: number; y: number };
  stats: KnowledgeFlowStats;
  stats_available: boolean;
  stats_source?: string | null;
  access_matrix: Record<string, string>;
  is_orphan: boolean;
  is_cold: boolean;
}

export interface KnowledgeFlowEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  direction: "uni" | "bi";
  access: "full" | "partial" | "none";
  stats: KnowledgeFlowStats;
  stats_available: boolean;
  stats_source?: string | null;
}

export interface KnowledgeFlowGraph {
  version: number;
  windows_days: number[];
  generated_at: string;
  layout?: string;
  nodes: KnowledgeFlowNode[];
  edges: KnowledgeFlowEdge[];
  meta: {
    gap_count: number;
    node_count: number;
    edge_count: number;
    ai_count: number;
    source_count: number;
  };
}

export interface PublishedReel {
  id: string;
  caption?: string;
  timestamp?: string;
  permalink?: string;
  thumbnail_url?: string;
  media_type?: string;
  downloadable?: boolean;
  already_in_library?: boolean;
  hint?: string;
}

export interface MediaListResponse {
  items: MediaAsset[];
  count: number;
  queued_jobs?: number;
  inbox?: string;
  inbox_reels?: string;
  ffmpeg?: boolean;
}

export interface MediaScanResponse {
  created?: number;
  reused?: number;
  skipped?: number;
  tagged?: { done?: number };
  errors?: string[];
}

export interface MediaSuggestBody {
  pain_id?: string;
  tool_id?: string;
  book_id?: string;
  funnel_stage?: string;
  need_person?: string;
  mode?: string;
  q?: string;
  title?: string;
  theme?: string;
  photo_kind?: string;
  copy_text?: string;
  limit?: number;
}

export interface ContentItem {
  id: string;
  kind: string;
  funnel_stage: string;
  week?: string;
  owner?: string;
  status?: string;
  title?: string;
  caption?: string;
  brief_text?: string;
  carousel_id?: string;
  filmed_at?: string;
}

export interface PublicationsHubChannelIg {
  present: boolean;
  impressions: number;
  reach: number;
  saved: number;
  shares: number;
  comments: number;
  save_rate: number;
}

export interface PublicationsHubChannelFb {
  present: boolean;
  impressions: number;
  reach: number;
  comments: number;
  clicks?: number;
}

export interface PublicationsHubItem {
  id: string;
  short_id?: string;
  kind: string;
  funnel_stage: string;
  title: string;
  published_at: string;
  week?: string;
  thumb_url?: string;
  ig_permalink?: string;
  fb_permalink?: string;
  via?: string;
  short_url?: string;
  impressions: number;
  reach: number;
  saved: number;
  shares: number;
  comments: number;
  comments_quality?: number;
  save_rate: number;
  share_rate: number;
  score: number;
  views_delta_1d?: number | null;
  ig: PublicationsHubChannelIg;
  fb: PublicationsHubChannelFb;
  sessions: number;
  sessions_via?: number;
  clicks_via?: number;
  checkouts: number;
  purchases: number;
  cvr_checkout: number;
  cvr_purchase: number;
  eval_label: string;
  eval_text: string;
  eval_at?: string;
  synced_at?: string;
  studio_href: string;
}

export interface PublicationsHub {
  window: { mode: string; week?: string | null; days?: number | null };
  synced_at?: string;
  sync_stale?: boolean;
  truncated?: boolean;
  kpis: {
    publications: number;
    impressions: number;
    reach: number;
    reach_ig: number;
    reach_fb: number;
    impressions_ig?: number;
    impressions_fb?: number;
    saved: number;
    shares: number;
    comments: number;
    sessions: number;
    checkouts: number;
    purchases: number;
    score_median: number;
    cvr_purchase: number;
  };
  funnel_mix: {
    counts: Record<string, number>;
    pct: Record<string, number>;
    target: Record<string, number>;
    warning?: string;
  };
  plan_vs_live: {
    week: string;
    show: boolean;
    planned_studio: number;
    published_studio: number;
    href: string;
    window_is_week?: boolean;
  };
  items: PublicationsHubItem[];
}

export interface WeekSlot {
  id?: string;
  weekday: number;
  kind: string;
  funnel_stage: string;
  owner: string;
  status?: string;
  content_item_id?: string;
  theme?: string;
  pain_id?: string;
  book_id?: string;
  tool_id?: string;
  hope_axis?: string;
}

export interface WeekPlanMeta {
  pain_id?: string;
  theme?: string;
  tool_id?: string;
  book_id?: string;
  arek_focus?: string;
  kasia_focus?: string;
  experiment?: string;
  rocks?: string[];
  cycle_phase?: string;
  buffer_day?: number;
}

export interface WeekCompassAxis {
  id: string;
  label: string;
  status: "ok" | "warn" | "fail" | string;
  detail: string;
  source: string;
}

export interface WeekCompass {
  overall?: string;
  summary?: string;
  axes?: WeekCompassAxis[];
  block_hints?: string[];
}

export interface WeekPlan {
  id: string;
  week: string;
  status: string;
  tofu_pct: number;
  mofu_pct: number;
  bofu_pct: number;
  mix_warning?: string;
  approved_at?: string;
  slots: WeekSlot[];
  mix_actual?: { counts: Record<string, number>; pct: Record<string, number>; total: number };
  mix_target?: Record<string, number>;
  theme?: { id?: string; theme?: string; pain_id?: string; tool_id?: string; book_id?: string };
  meta?: WeekPlanMeta;
  compass?: WeekCompass;
  day_labels?: string[];
}

export type ArekWeekSection = "rank" | "fokus" | "utrzymanie" | "nauka" | "zamkniecie" | string;
export type ArekWeekCategory = "nauka" | "biznes" | "ops" | string;

export interface ArekWeekItem {
  id: string;
  text: string;
  section: ArekWeekSection;
  category?: ArekWeekCategory;
  done?: boolean;
}

export interface ArekWeekDay {
  weekday: number;
  label: string;
  role: string;
  items: ArekWeekItem[];
}

export interface ArekWeekHeader {
  cycle_phase?: string;
  pain?: string;
  tool?: string;
  focus?: string;
  experiment?: string;
  learning_question?: string;
  mode?: "norma" | "minimalna" | string;
}

export interface ArekWeekFooter {
  result?: string;
  red_metric?: string;
  decision?: string;
  parking?: string;
}

export interface ArekWeekTemplate {
  version?: number;
  days: ArekWeekDay[];
  limits?: string[];
  updated_at?: string;
}

export interface ArekWeekInstance {
  week: string;
  header: ArekWeekHeader;
  days: ArekWeekDay[];
  footer: ArekWeekFooter;
  limits?: string[];
  updated_at?: string;
}

export interface CarouselInspirationBorrow {
  layout?: boolean;
  idea?: boolean;
  hierarchy?: boolean;
  colors?: boolean;
  photo_kind?: boolean;
}

export interface CarouselInspirationAnalysis {
  idea?: string;
  layout_note?: string;
  format_id?: string;
  pattern?: string;
  slide_count?: number;
  hierarchy?: string;
  colors?: string;
  photo_kind?: string;
  borrow_suggest?: string[];
  do_not_copy?: string;
  seen?: boolean;
  error?: string;
}

export interface CarouselBrief {
  week?: string;
  book_id?: string;
  pain_id?: string;
  tool_id?: string;
  sku?: string;
  style_id?: string;
  visual_pack_id?: string;
  format_id?: string;
  type_id?: string;
  channel?: string;
  use_book?: boolean;
  use_comments?: boolean;
  use_messenger?: boolean;
  use_stories?: boolean;
  inspiration_urls?: string[];
  inspiration_note?: string;
  inspiration_borrow?: CarouselInspirationBorrow;
  draft_text?: string;
  draft_edit_mode?: "literal" | "hook_close" | "reach" | string;
  compose_mode?: "operator" | "ai" | "";
  from_draft?: boolean;
  skip_concepts?: boolean;
  board_label?: string;
  from_permalink?: string;
  kind?: "carousel" | "feed_post" | "czasotekst";
  funnel_stage?: "tofu" | "mofu" | "bofu";
  czasotekst_form_id?: string;
  czasotekst_variant_id?: string;
  cta_intent?: "save" | "send" | "comment" | "";
  close_mode?: "caption_only" | "soft_action" | "slide_cta" | "";
  ab_variable?: "hook" | "length" | "cover" | "cta" | "";
  ab_pair_id?: string;
  bg_mode?: "flat" | "gemini";
  bg_hint?: string;
  bg_preset_id?: string;
  bg_placement_id?: string;
  layout_preset_id?: string;
  style_apply_mode?: "bg" | "full" | "layout";
}

export interface CarouselGenerationRun {
  id: string;
  carousel_id: string;
  short_id?: string;
  step: string;
  created_at: string;
  source?: string;
  model?: string;
  error?: string;
  prompt?: string;
  raw?: unknown;
  pack?: string;
  brief?: Record<string, unknown>;
  sources?: CarouselSourceHit[];
  ideas?: Partial<CarouselIdea>[];
  discussion?: Record<string, unknown>;
  copy?: Record<string, unknown>;
  slides?: unknown[];
  caption?: string;
  usage?: { in?: number; out?: number; think?: number; usd?: number };
}

export interface CarouselIdea {
  id: string;
  rank: number;
  hook: string;
  concept?: string;
  effect?: string;
  structure: string;
  scene: string;
  tool_line: string;
  format_source: string;
  format_note: string;
  format_id?: string;
  type_id?: string;
  selected?: boolean;
}

export interface CarouselSlideLayoutBox {
  id?: string;
  role?: string;
  text?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  font?: string;
  size?: number;
  color?: string;
  align?: string;
  panel?: boolean;
  panel_color?: string;
  panel_opacity?: number;
  visible?: boolean;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  highlight?: string;
  marks?: {
    start?: number;
    end?: number;
    highlight?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    color?: string;
  }[];
  arrow_style?:
    | "soft_chevron"
    | "ink_double"
    | "brush"
    | "quill"
    | "ink_wave"
    | "hand_arc"
    | "fat_swoosh"
    | "ink_hook"
    | "ink_scribble"
    | "brush_line"
    | "sketch_wedge"
    | "ribbon"
    | "classic_ink"
    | "blade"
    | "curl"
    | "double_quill"
    | "slant_sketch"
    | "fat_marker"
    | "thin_line"
    | "ornate"
    | "chevron"
    | "filled"
    | "line"
    | "double"
    | "wiggle"
    | "sketch"
    | "curve"
    | "swoosh"
    | "hook"
    | "rough"
    | "check"
    | "star"
    | "heart"
    | "plus"
    | "circle"
    | "dot"
    | "x"
    | string;
  rotate?: number;
  bullet_icon?: string;
}

export interface CarouselSlideLayout {
  w?: number;
  h?: number;
  type?: string;
  recipe?: string;
  list_icon?: string;
  bullet_icon?: string;
  bg?: { x?: number; y?: number; scale?: number };
  boxes?: CarouselSlideLayoutBox[];
}

export interface CarouselSlide {
  id: string;
  index_n: number;
  slide_type: string;
  png_relpath?: string;
  bg_relpath?: string;
  media_kind?: string;
  mp4_relpath?: string;
  layout?: CarouselSlideLayout | null;
  copy?: Record<string, unknown>;
  qa_ok?: boolean;
  qa_note?: string;
}

export interface CarouselLayoutQaFail {
  index: number;
  reason: string;
}

export interface CarouselLayoutQa {
  ok?: boolean;
  failed?: CarouselLayoutQaFail[];
  retried?: boolean;
  vision?: boolean;
  summary?: string;
}

export interface CarouselDesignVoice {
  role?: string;
  text?: string;
}

export interface CarouselComposeScore {
  ok?: boolean;
  fail_count?: number;
  fails?: Record<string, number[]>;
  occupancy?: { index?: number; value?: number }[];
}

export interface CarouselComposeLoopTurn {
  turn?: number;
  analyze?: string;
  now_score?: CarouselComposeScore;
  after_score?: CarouselComposeScore;
  actions?: string[];
  delta?: { improved?: string[]; worse?: string[]; left?: string[]; empty?: boolean };
  diagnosis?: string;
  now_rel?: string;
  after_rel?: string;
}

export interface CarouselComposeLogEntry {
  at?: string;
  kind?: string;
  turn?: number;
  text?: string;
  prompt?: string;
  raw?: string;
  ops?: string[];
  score?: CarouselComposeScore;
  model?: string;
}

export interface CarouselComposeLoop {
  turn?: number;
  status?: string;
  stop_reason?: string;
  turns?: CarouselComposeLoopTurn[];
  log?: CarouselComposeLogEntry[];
}

export interface CarouselDesignReview {
  pass?: number;
  verdict?: string;
  ok?: boolean;
  diagnosis?: string;
  preserve?: string;
  panel?: CarouselDesignVoice[];
  actions?: Record<string, unknown>[];
  proposed_actions?: Record<string, unknown>[];
  proposed?: {
    op?: string;
    label?: string;
    hint?: string;
    group?: string;
    choice?: "always" | "radio" | "check" | string;
    index?: number;
    box?: string;
    recipe?: string;
  }[];
  status?: string;
  applied?: string[];
  rejected_actions?: Record<string, unknown>[];
  rejected?: string[];
  verify?: { verdict?: string; note?: string; panel?: CarouselDesignVoice[] };
  rating?: string;
  feedback?: string;
  error?: string;
  before_rel?: string;
  after_rel?: string;
  skipped?: boolean;
  loop?: CarouselComposeLoop;
  house_score?: CarouselComposeScore;
  house_note?: string;
  series_align?: string;
}

export interface CarouselLayoutPlanSlide {
  index?: number;
  recipe?: string;
  why?: string;
}

export interface CarouselLayoutPlan {
  pattern?: string;
  format_id?: string;
  rationale?: string;
  source?: string;
  slides?: CarouselLayoutPlanSlide[];
}

export interface CarouselChangeProposal {
  id: string;
  carousel_id?: string;
  retro_id?: string;
  source?: string;
  target: string;
  patch?: { value?: string; old?: string; new?: string };
  why?: string;
  roles?: Record<string, string>;
  status?: string;
  applied_at?: string;
}

export type CarouselPublishChannel = "ig" | "fb" | "both";
export type CarouselFbPublishMode = "gallery" | "slide1" | "reel";

export interface CarouselPublishRun {
  id: string;
  carousel_id: string;
  created_at: string;
  channel: CarouselPublishChannel | string;
  fb_format: CarouselFbPublishMode | string;
  ig_media_id?: string;
  ig_permalink?: string;
  fb_post_id?: string;
  fb_permalink?: string;
  reel_relpath?: string;
  ok: boolean;
  error?: string;
  meta?: Record<string, unknown>;
}

export interface CarouselItem {
  id: string;
  short_id: string;
  status: string;
  week: string;
  book_id: string;
  pain_id: string;
  tool_id: string;
  sku: string;
  style_id?: string;
  visual_pack_id?: string;
  format_id?: string;
  type_id?: string;
  czasotekst_form_id?: string;
  czasotekst_variant_id?: string;
  bg_unique_count?: number;
  bg_mode?: "flat" | "gemini";
  bg_hint?: string;
  bg_preset_id?: string;
  bg_placement_id?: string;
  layout_preset_id?: string;
  style_apply_mode?: "bg" | "full" | "layout";
  layout_qa?: CarouselLayoutQa | null;
  design_review?: CarouselDesignReview | null;
  design_feedback?: string;
  bullet_icon?: string;
  reel_track_id?: string;
  reel_track_title?: string;
  epidemic_license_ref?: string;
  reel_relpath?: string;
  reel_status?: string;
  reel_error?: string;
  reel_progress?: { percent?: number; detail?: string; phase?: string; updated_at?: string } | null;
  has_reel?: boolean;
  reel_url?: string;
  channel: string;
  fb_publish_mode?: CarouselFbPublishMode;
  publish_runs?: CarouselPublishRun[];
  title: string;
  hook: string;
  caption: string;
  caption_engage: string;
  caption_feedback?: string;
  caption_verify?: boolean;
  via: string;
  landing_url: string;
  short_url?: string;
  scheduled_at?: string;
  ig_media_id?: string;
  fb_post_id?: string;
  ig_permalink?: string;
  fb_permalink?: string;
  publish_error?: string;
  publish_progress?: CarouselPublishProgress | null;
  render_status?: string;
  render_error?: string;
  render_done?: number;
  render_total?: number;
  render_cancel?: boolean;
  render_progress?: CarouselRenderProgress | null;
  bg_cand_progress?: CarouselBgCandProgress | null;
  updated_at?: string;
  quality_flags?: string[];
  selected_idea_id?: string;
  copy_approved_at?: string;
  llm_status?: string;
  llm_detail?: string;
  copy_feedback?: string;
  celery_task_id?: string;
  llm_progress?: CarouselLlmProgress | null;
  rating?: string;
  concepts_source?: string;
  sources?: CarouselSourceHit[];
  latest_generation?: CarouselGenerationRun | null;
  generation_runs?: CarouselGenerationRun[];
  copy?: { slides?: Record<string, unknown>[]; caption?: string; title?: string };
  ideas?: CarouselIdea[];
  slides?: CarouselSlide[];
  inspiration?: {
    type?: string;
    url?: string;
    file?: string;
    files?: string[];
    needsUpload?: boolean;
    error?: string;
  }[];
  inspiration_files?: string[];
  inspiration_borrow?: CarouselInspirationBorrow;
  inspiration_analysis?: CarouselInspirationAnalysis;
  insights?: {
    score?: number;
    reach?: number;
    saved?: number;
    shares?: number;
    clicks_via?: number;
    save_rate?: number;
    share_rate?: number;
    deep_action_rate?: number;
    er_by_reach?: number;
  } | null;
  cta_intent?: string;
  close_mode?: string;
  ab_variable?: string;
  ab_pair_id?: string;
  discussion?: {
    panel?: { role?: string; text?: string }[];
    verdict?: string;
    rounds?: number;
    copy_revision?: {
      summary?: string;
      feedback?: string;
      changes?: { slide?: number | null; note?: string }[];
      before_slides?: { type?: string; text?: string; sub?: string }[];
      after_slides?: { type?: string; text?: string; sub?: string }[];
    };
    copy_baseline?: {
      slides?: { type?: string; text?: string; sub?: string }[];
      caption?: string;
      captionEngage?: string;
      title?: string;
    };
    copy_review?: {
      verdict?: string;
      panel?: { role?: string; text?: string }[];
      patches?: { index?: number; text?: string; sub?: string; why?: string }[];
      rejected_ids?: string[];
      actions?: {
        id?: string;
        kind?: string;
        slide?: number | null;
        label?: string;
        detail?: string;
        before?: { text?: string; sub?: string };
        after?: { text?: string; sub?: string };
        patch?: { index?: number; text?: string; sub?: string; why?: string };
      }[];
      error?: string;
    };
    copy_chat?: {
      role?: string;
      text?: string;
      at?: string;
      kind?: string;
      patches?: { slide?: number; text?: string; sub?: string; left?: string; right?: string }[];
    }[];
    copy_chat_undo?: { copy?: Record<string, unknown> } | null;
    caption_chat?: {
      role?: string;
      text?: string;
      at?: string;
      kind?: string;
    }[];
    caption_chat_undo?: {
      caption?: string;
      caption_engage?: string;
      hook?: string;
      hook_on_screen?: string;
      caption_cta?: Record<string, unknown> | null;
      quality_flags?: string[];
    } | null;
    caption_chat_pending?: {
      hook_on_screen?: string;
      caption?: string;
      captionEngage?: string;
    } | null;
    draft_critique?: {
      verdict?: "ship" | "fix" | "kill" | string;
      scores?: {
        hook?: number;
        emotion?: number;
        swipe?: number;
        promise?: number;
        cta?: number;
        risks?: string[];
      };
      brutal?: string;
      must_change?: string[];
      keep?: string[];
      hook_hardened?: boolean;
      error?: string;
    };
    caption_cta?: {
      verdict?: string;
      claim?: string;
      note?: string;
      hits?: { book_id?: string; label?: string; excerpt?: string; source?: string }[];
    };
  };
  usage?: CarouselUsage;
  pipeline?: CarouselPipeline;
  draft_text?: string;
  draft_edit_mode?: "literal" | "hook_close" | "reach" | string;
  compose_mode?: "operator" | "ai" | "";
  layout_plan?: CarouselLayoutPlan | null;
  snapshot?: { id?: string; kind?: string; output?: Record<string, unknown> } | null;
  change_proposals?: CarouselChangeProposal[];
  propagate?: {
    changed?: number[];
    skipped?: number[];
    compose_failed?: number[];
    mode?: string;
    box_id?: string;
    params?: string[] | null;
  };
  lessons?: CarouselLesson[];
  retro?: {
    id?: string;
    verdict?: string;
    decision?: string;
    transcript?: Record<string, string>;
    phase?: string;
  } | null;
  retros?: Array<{
    id?: string;
    phase?: string;
    verdict?: string;
    decision?: string;
    transcript?: Record<string, string>;
  }>;
  retro_discussion?: {
    id?: string;
    verdict?: string;
    decision?: string;
    transcript?: Record<string, string>;
    phase?: string;
  } | null;
  retro_results?: {
    id?: string;
    verdict?: string;
    decision?: string;
    transcript?: Record<string, string>;
    phase?: string;
  } | null;
  kind?: "carousel" | "feed_post" | string;
  funnel_stage?: "tofu" | "mofu" | "bofu" | string;
  content?: ContentItem | null;
}

export interface CarouselCreateResponse {
  item: CarouselItem;
  short_url: string;
  tracked_url: string;
  warnings?: string[];
}

export interface CarouselListResponse {
  items: CarouselItem[];
  count: number;
}

export interface CarouselCatalogFormat {
  id: string;
  label: string;
  hint?: string;
  structure?: string[];
  close?: string;
  prompt_key?: string;
}

export interface CarouselCatalogStyle {
  id: string;
  label: string;
  hint?: string;
  bg?: string;
  accent?: string;
  ink?: string;
  font?: string;
  prompt?: string;
}

export interface CarouselCatalogVisualPack {
  id: string;
  label: string;
  hint?: string;
  source_folders?: string[];
  style_id?: string;
  beats?: Record<string, { recipe?: string; align?: string; panel?: boolean; why?: string }>;
}

export interface CarouselCatalogBgPreset {
  id: string;
  label: string;
  hint?: string;
  swatch?: string;
  family?: string;
  flat_bg?: string;
  prompt?: string;
  allow?: string[];
  source_folders?: string[];
  style_id?: string;
  visual_pack_id?: string;
}

export interface CarouselCatalogBgPlacement {
  id: string;
  label: string;
  hint?: string;
  prompt?: string;
}

export interface CarouselCatalogCzasotekstForm {
  id: string;
  label: string;
  icon?: string;
  structure?: string;
  default_funnel?: "tofu" | "mofu" | "bofu" | string;
  target_words?: string;
  tip?: string;
  blurb?: string;
}

export interface CzasotekstFramePreset {
  id: string;
  label: string;
  panel?: boolean;
  radius?: number;
  padding?: number;
  shadow?: boolean;
  shadow_hard?: boolean;
  ink?: string;
  bg?: string;
  border?: string;
  border_w?: number;
  anchor?: string;
}

export interface CzasotekstOverlayBox {
  x: number;
  y: number;
  w: number;
  h: number;
  rotate?: number;
}

export interface CzasotekstOverlayText {
  font?: string;
  size?: number;
  color?: string;
  align?: "left" | "center" | "right" | string;
}

export interface CzasotekstOverlay {
  frame_id?: string;
  box?: CzasotekstOverlayBox;
  text?: CzasotekstOverlayText;
}

export interface CarouselCatalogCzasotekstVariant {
  id: string;
  label: string;
  blurb?: string;
  hypothesis?: string;
  prompt_block?: string;
  target_words_override?: string | null;
  engage_mode?: "any" | "save_share" | "discuss" | string;
  frontload_chars?: number;
  recommend?: { funnels?: string[]; forms?: string[]; weight?: number };
}

export interface CarouselCatalogCzasotekstBed {
  id: string;
  label: string;
  file: string;
  bpm?: number;
  default?: boolean;
}

export interface CarouselCatalog {
  books?: { id: string; label: string }[];
  pains?: { id: string; label: string }[];
  tools?: { id: string; label: string; book_id?: string; excerpt?: string; source?: string }[];
  skus?: { id: string; label: string }[];
  weeks?: { id: string; theme?: string }[];
  formats?: CarouselCatalogFormat[];
  types?: CarouselCatalogFormat[];
  styles?: CarouselCatalogStyle[];
  visual_packs?: CarouselCatalogVisualPack[];
  bg_presets?: CarouselCatalogBgPreset[];
  bg_placements?: CarouselCatalogBgPlacement[];
  czasotekst_forms?: CarouselCatalogCzasotekstForm[];
  czasotekst_frames?: CzasotekstFramePreset[];
  czasotekst_variants?: CarouselCatalogCzasotekstVariant[];
  czasotekst_beds?: CarouselCatalogCzasotekstBed[];
}

export interface CarouselSourceHit {
  type?: string;
  kind?: string;
  id?: string;
  excerpt?: string;
  meta?: string;
  hook?: string;
  reveal?: string;
  beats?: string[];
  used_count?: number;
  last_used_at?: string;
  usage_label?: string;
}

export interface CarouselSourcesResponse {
  items: CarouselSourceHit[];
  count: number;
}

export interface EpidemicTrack {
  id: string;
  title: string;
  artist: string;
  length_sec?: number;
  is_preview_only?: boolean;
  image?: string;
  preview_path?: string;
}

export interface EpidemicStatus {
  configured: boolean;
  state: string;
  can_search?: boolean;
  can_download?: boolean;
  hint?: string;
}

export interface CarouselStudioStatus {
  gemini_ready: boolean;
  queue_paused: boolean;
  publish: {
    can_publish_ig?: boolean;
    can_publish_fb?: boolean;
    missing_ig?: string[];
    missing_fb?: string[];
    using_page_token?: boolean;
    fb_hint?: string;
    ffmpeg?: boolean;
    epidemic?: EpidemicStatus;
  };
}

export interface CarouselRankingResponse {
  items: { carousel_id: string; score: number; title?: string }[];
  bias: Record<string, number>;
}

export interface CarouselPublishSlot {
  suggested_at: string;
  local: string;
  label: string;
  reason: string;
  windows: { days: string; hour: number; why: string }[];
  learned?: { weekday: number; hour: number; n: number; avg_score: number }[];
  busy?: { id?: string; title?: string; status?: string; at?: string }[];
  sample?: { published: number; at_night: number; enough: boolean };
}

export interface CarouselPromptSetting {
  key: string;
  value: string;
  source: string;
  file_default?: string;
}

export interface CarouselPromptsResponse {
  items: CarouselPromptSetting[];
  placeholders: string[];
}

export interface CarouselLesson {
  id: string;
  carousel_id?: string;
  retro_id?: string;
  kind: string;
  text: string;
  status: string;
  source?: string;
}

export interface CarouselUsageEvent {
  step: string;
  model: string;
  in: number;
  out: number;
  think: number;
  images: number;
  usd: number;
}

export interface CarouselPublishStep {
  id?: string;
  label?: string;
  state?: "pending" | "active" | "done" | "error" | string;
  detail?: string;
}

export interface CarouselPublishProgress {
  started_at?: string;
  updated_at?: string;
  phase?: "publishing" | "done" | "failed" | string;
  step_id?: string;
  label?: string;
  detail?: string;
  done?: number;
  total?: number;
  percent?: number;
  slide_count?: number;
  channel?: string;
  error?: string;
  steps?: CarouselPublishStep[];
}

export interface CarouselRenderProgress {
  started_at?: string;
  slide_started_at?: string;
  updated_at?: string;
  done?: number;
  total?: number;
  expected_ms_per_slide?: number;
  samples?: number;
  percent?: number;
  eta_sec?: number;
  ms_per_slide?: number;
  phase?: "queued" | "waiting" | "rendering" | "done" | "cancelled" | "failed" | string;
}

export interface CarouselLlmEvent {
  at?: string;
  event?: string;
  model?: string;
  detail?: string;
  chars?: number;
  count?: number;
  ticks?: number;
}

export interface CarouselLlmSegment {
  id?: string;
  label?: string;
  weight_ms?: number;
  fraction?: number;
}

export interface CarouselBgCandProgress {
  started_at?: string;
  variant_started_at?: string;
  updated_at?: string;
  done?: number;
  total?: number;
  current?: number;
  index_n?: number;
  expected_ms_per_variant?: number;
  job_expected_ms?: number;
  samples?: number;
  percent?: number;
  eta_sec?: number;
  ms_per_variant?: number;
  detail?: string;
  error?: string;
  segments?: CarouselLlmSegment[];
  phase?: "waiting" | "generating" | "done" | "error" | "failed" | string;
}

export interface CarouselLlmProgress {
  step?: string;
  started_at?: string;
  step_started_at?: string;
  updated_at?: string;
  received_chars?: number;
  expected_chars?: number;
  expected_ms?: number;
  job_expected_ms?: number;
  job_steps?: string[];
  remaining_steps?: string[];
  segments?: CarouselLlmSegment[];
  samples?: number;
  percent?: number;
  chars_per_sec?: number;
  eta_sec?: number;
  elapsed_ms?: number;
  stall_sec?: number;
  awaiting_text_sec?: number;
  awaiting_text_limit_sec?: number;
  awaiting_text_since?: string;
  hint?: string;
  last_event?: string;
  model?: string;
  prompt?: string;
  prompt_chars?: number;
  events?: CarouselLlmEvent[];
  phase?: "waiting" | "receiving" | "done" | string;
}

export interface CarouselUsage {
  events?: CarouselUsageEvent[];
  totals?: { in: number; out: number; think: number; images: number; usd: number };
}

export interface CarouselPipelineStage {
  id: string;
  label: string;
  icon: string;
  state: string;
  percent: number;
  detail: string;
  anchor: string;
}

export interface CarouselPipeline {
  stages: CarouselPipelineStage[];
  now: string;
  active_id?: string;
  llm_status?: string;
  llm_detail?: string;
  cost_label?: string;
  usage?: CarouselUsage;
}

export interface Subsystem {
  name: string;
  state: "ok" | "warn" | "down" | "off";
  last_seen: string | null;
  last_error?: string | null;
  note?: string | null;
}

export interface GeminiBillingSnapshot {
  balance_usd: number | null;
  set_at_utc: string | null;
  note: string | null;
  spent_since_set: number | null;
  remaining_usd: number | null;
  daily: { date: string; usd: number }[];
  avg_daily_usd: number;
  days_left: number | null;
  depletes_on: string | null;
  sources_note: string;
  key_present: boolean;
}

export interface SalesSummary {
  dzis_pln: number;
  dni7_pln: number;
  dni30_pln: number;
  ytd_pln: number;
  paid_orders_30d: number;
  free_orders_30d: number;
  customers_30d: number;
  per_product: Record<string, number>;
  transactions: {
    date: string;
    produkt: string;
    kwota: number;
    kanal: string | null;
    netto: number | null;
  }[];
}

export interface SalesDailyPoint {
  date: string;
  revenue: number;
  paid_orders: number;
}

export interface SalesDailyResponse {
  ok: boolean;
  days: number;
  timezone?: string;
  since?: string;
  series: SalesDailyPoint[];
}

export type OpsEventSource =
  | "manual"
  | "auto_landing_deploy"
  | "auto_landing_beacon"
  | "auto_agent";

export interface OpsEventTypeMeta {
  id: string;
  label: string;
  group: string;
  fields: string[];
}

export interface OpsEventTypesResponse {
  types: OpsEventTypeMeta[];
}

export interface OpsEvent {
  id: string;
  event_date: string;
  event_type: string;
  source: OpsEventSource | string;
  title: string;
  notes: string;
  qty: number;
  attrs: Record<string, unknown>;
  dedupe_key?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  hidden: boolean;
}

export interface OpsEventsListResponse {
  from: string;
  to: string;
  items: OpsEvent[];
  count: number;
}

export interface OpsEventCreateBody {
  event_date: string;
  event_type: string;
  title?: string;
  notes?: string;
  qty?: number;
  attrs?: Record<string, unknown>;
  source?: OpsEventSource | string;
  created_by?: string;
  dedupe_key?: string | null;
}

export interface OpsEventUpdateBody {
  event_date?: string;
  event_type?: string;
  title?: string;
  notes?: string;
  qty?: number;
  attrs?: Record<string, unknown>;
  hidden?: boolean;
}

export type KasiaSummaryStatus = "lepiej" | "gorzej" | "podobnie" | "malo_danych";

export interface KasiaSummaryCard {
  id: string;
  label: string;
  value: number;
  previous: number;
  value_display: string;
  compare_text: string;
  status: KasiaSummaryStatus;
  kind: "money" | "count" | string;
}

export interface KasiaSummaryVerdict {
  status: KasiaSummaryStatus;
  text: string;
}

export interface KasiaSummaryDeedChip {
  type: string;
  label: string;
  count: number;
}

export interface KasiaSummaryDeedEvent {
  type: string;
  label: string;
  title: string;
  notes: string;
  qty: number;
}

export interface KasiaSummaryResponse {
  ok: boolean;
  days: number;
  period: {
    label_pl: string;
    prev_label_pl: string;
    date_from: string;
    date_to: string;
    prev_date_from: string;
    prev_date_to: string;
  };
  cards: KasiaSummaryCard[];
  verdicts: KasiaSummaryVerdict[];
  deeds: {
    chips: KasiaSummaryDeedChip[];
    by_day: { date: string; events: KasiaSummaryDeedEvent[] }[];
    empty_hint: string;
    prev_chips?: KasiaSummaryDeedChip[];
  };
  daily: {
    series: { date: string; revenue: number; orders: number }[];
    best_day?: {
      date: string;
      revenue: number;
      orders: number;
      deeds_text?: string;
    } | null;
  };
  goal_track?: KasiaGoalTrack;
}

export type KasiaGoalPace = "powyzej" | "ponizej" | "blisko";

export interface KasiaGoalTrackWeek {
  iso_week: string;
  label_pl: string;
  date_from: string;
  date_to: string;
  revenue: number;
  is_partial: boolean;
}

export interface KasiaGoalTrack {
  goal_monthly_pln: number;
  goal_weekly_pln: number;
  month: {
    label_pl: string;
    revenue_mtd: number;
    days_elapsed: number;
    days_in_month: number;
    days_remaining?: number;
    pct_of_goal: number;
    projected_pln: number;
    avg_daily_lookback: number;
    lookback_weeks: number;
    pace_vs_goal: KasiaGoalPace;
  };
  weeks: KasiaGoalTrackWeek[];
}

export interface SiteAnalyticsSummary {
  generated_at: string;
  window_days: number;
  ga4: {
    ok: boolean;
    error?: string;
    top_traffic_sources?: Record<string, string | number>[];
    top_landing_pages?: Record<string, string | number>[];
    device_breakdown?: Record<string, string | number>[];
    engagement_summary?: Record<string, string | number>[];
    site_event_counts?: Record<string, string | number>[];
    cta_breakdown?: Record<string, string | number>[];
    section_dropoff?: Record<string, string | number>[];
    scroll_depth_summary?: Record<string, string | number>[];
    product_funnel?: Record<string, string | number>[];
    pending_custom_dimensions?: string[];
    note_custom_dimensions?: string;
  };
  clarity: {
    ok: boolean;
    error?: string;
    note?: string;
    overview?: unknown[];
    by_device?: unknown[];
    by_source?: unknown[];
    by_page?: unknown[];
    top_issues_sample?: Record<string, unknown>[];
    quota?: { requests_used_today: number; self_imposed_limit: number; remaining_today: number };
    warning?: string;
    cached?: boolean;
  };
  commerce: {
    ok: boolean;
    error?: string;
    real_paid_orders?: number;
    real_revenue?: number;
    ga4_purchase_events?: number;
    ga4_vs_real_delta?: number;
    ga4_begin_checkout_events?: number;
    ga4_add_to_cart_events?: number;
    note?: string;
  };
}

/** Query params for GET /api/site-analytics/journey */
export interface SiteAnalyticsJourneyParams {
  days?: number;
  /** today | yesterday | 7 | 14 | 30 */
  period?: string;
  /** entry_page / entry point, e.g. apex_home, sub_pakiet */
  entry?: string;
  /** new | returning */
  visitor?: string;
  /** paid | organic_social | other */
  traffic?: string;
  /** experiment_version label */
  version?: string;
  /** data-page / page_id podstrony */
  page_id?: string;
  /** first-party ?via=… (bio, manychat, …) */
  via?: string;
}

export type CustomerJourneySourceMode = "channel" | "via";

export interface CustomerJourneyMapParams extends SiteAnalyticsJourneyParams {
  source_mode?: CustomerJourneySourceMode;
}

export interface CustomerJourneyMapNode {
  id: string;
  layer: "source" | "landing" | "mid" | "end" | string;
  key: string;
  label: string;
  sessions: number;
  purchases?: number;
  share?: number;
  cvr?: number;
}

export interface CustomerJourneyMapEdge {
  id: string;
  source: string;
  target: string;
  sessions: number;
  purchases?: number;
  cvr?: number;
  share_of_source?: number;
}

export interface CustomerJourneyMapLeak {
  edge_id?: string;
  source?: string;
  target?: string;
  source_label?: string;
  target_label?: string;
  sessions: number;
  share_of_source?: number;
  drop_rate?: number;
}

export interface CustomerJourneyMapWhenHour {
  hour: number;
  label: string;
  orders: number;
  revenue: number;
}

export interface CustomerJourneyMapWhenDow {
  dow: number;
  label: string;
  orders: number;
  revenue: number;
}

export interface CustomerJourneyMapProduct {
  slug: string;
  label: string;
  orders: number;
  revenue: number;
  share?: number;
}

export interface CustomerJourneyMapResponse {
  ok: boolean;
  generated_at?: string;
  window_days?: number;
  source_mode?: CustomerJourneySourceMode;
  period?: {
    days?: number;
    since?: string;
    until?: string | null;
    key?: string;
    timezone?: string;
    date_from?: string;
    date_to?: string;
    label_pl?: string;
    sessions?: number;
  };
  kpi?: {
    sessions: number;
    cart_sessions: number;
    purchase_sessions_journey?: number;
    easycart_paid_orders?: number;
    purchases: number;
    cvr_session_to_purchase: number;
    cvr_cart_to_purchase: number;
    purchase_ssot?: string;
  };
  nodes: CustomerJourneyMapNode[];
  edges: CustomerJourneyMapEdge[];
  leaks?: CustomerJourneyMapLeak[];
  when?: {
    timezone?: string;
    by_hour?: CustomerJourneyMapWhenHour[];
    by_dow?: CustomerJourneyMapWhenDow[];
    peak_hour?: CustomerJourneyMapWhenHour | null;
    peak_dow?: CustomerJourneyMapWhenDow | null;
    orders?: number;
    note_pl?: string;
  };
  how?: {
    products?: CustomerJourneyMapProduct[];
    orders?: number;
    revenue?: number;
    aov?: number;
    note_pl?: string;
  };
  gaps_pl?: string[];
  layers?: { id: string; label: string }[];
  drilldown?: {
    lejki?: string;
    sciezki?: string;
    sesje?: string;
    rolki?: string;
    note_pl?: string;
  };
}

export type JourneyVisitorType = "new" | "returning";
export type JourneyTrafficClass = "paid" | "organic_social" | "other";

export type JourneyProfileId =
  | "impulse_buy"
  | "read_and_buy"
  | "checkout_abandon"
  | "deep_no_buy"
  | "shallow_no_buy"
  | "accidental_exit"
  | "explore_no_buy"
  | string;

export interface JourneyConversionCell {
  entry_page: string;
  /** Czytelny tytuł wejścia (UI) */
  entry_label?: string | null;
  visitor_type: JourneyVisitorType | string;
  traffic_class: JourneyTrafficClass | string;
  traffic_label?: string | null;
  sessions: number;
  /** 0–1 */
  checkout_rate: number;
  /** 0–1 */
  purchase_rate: number;
  revenue?: number | null;
  /** udział impulse_buy wśród zakupów w komórce (0–1) */
  impulse_buy_share?: number | null;
}

export interface JourneyVersionRow {
  experiment_version: string;
  content_sig?: string | null;
  content_sigs?: string[];
  sessions: number;
  checkout_rate: number;
  purchase_rate: number;
  impulse_buy_rate?: number | null;
  accidental_exit_rate?: number | null;
  /** mediana max scroll 0–100 */
  median_scroll?: number | null;
  /** mediana ms do pierwszego CTA */
  median_ms_to_cta?: number | null;
}

export interface JourneyPageVersionRegistryRow {
  page_id: string;
  experiment_version: string;
  content_sig?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  event_count?: number;
  note?: string;
}

export interface JourneyPageVersions {
  page_id?: string | null;
  label?: string | null;
  mode_hint?: "all" | "compare" | string;
  pages?: { page_id: string; label: string }[];
  registry?: JourneyPageVersionRegistryRow[];
  all_versions?: JourneyVersionRow | null;
  by_version?: JourneyVersionRow[];
}

export interface JourneyCohortProfile {
  profile: JourneyProfileId;
  sessions: number;
  /** udział wśród sesji 0–1 */
  share: number;
  median_active_ms?: number | null;
  median_scroll?: number | null;
  median_ms_to_cta?: number | null;
  top_exit_sections?: string[];
  traffic_mix?: Record<string, number>;
  checkout_rate?: number | null;
  purchase_rate?: number | null;
}

export interface JourneyBuyCompareSide {
  sessions: number;
  median_active_ms?: number | null;
  median_scroll?: number | null;
  median_ms_to_cta?: number | null;
  distinct_sections_median?: number | null;
  top_sections?: string[];
  traffic_mix?: Record<string, number>;
}

export interface JourneyImpulseCard {
  impulse_share_of_purchases: number;
  impulse_share_social?: number | null;
  impulse_share_ads?: number | null;
  purchases?: number | null;
  note?: string | null;
}

export interface JourneySectionReach {
  section_id: string;
  /** % sesji, które zobaczyły sekcję (0–1) */
  view_rate: number;
  view_rate_purchased?: number | null;
  view_rate_no_purchase?: number | null;
  /** indeks lift purchase vs no_purchase (np. 1.2 = +20%) */
  lift_purchase?: number | null;
}

export interface JourneyCtaReach {
  intent: string;
  label?: string | null;
  clicks: number;
  share: number;
  median_ms_to_click?: number | null;
  profile_mix?: Record<string, number>;
}

export interface JourneyFunnelStep {
  step: string;
  label?: string | null;
  sessions: number;
  /** udział względem pierwszej / wszystkich sesji (0–1) */
  rate: number;
}

export interface JourneyCompareFunnelStep {
  id: string;
  n: number;
  pct_of_entry: number;
  pct_of_prev: number;
}

export interface JourneyCompareFunnelDrop {
  from: string;
  to: string;
  n: number;
  pct: number;
}

export interface JourneyProductLandingSplit {
  page_id: string;
  entry_page: string;
  label: string;
  sessions: number;
  reached_cart: number;
  cart_rate: number;
}

export interface JourneyCompareFunnel {
  key: string;
  label: string;
  sessions: number;
  enough_data: boolean;
  steps: JourneyCompareFunnelStep[];
  drops: JourneyCompareFunnelDrop[];
  landings?: JourneyProductLandingSplit[];
  easycart?: {
    paid_orders?: number;
    revenue?: number;
    journey_purchases?: number;
    purchase_source?: string;
  };
}

export interface JourneyExitMixRow {
  profile: string;
  sessions: number;
  share: number;
}

export interface JourneyFunnelsCompare {
  steps: string[];
  min_sessions?: number;
  purchase_ssot?: string;
  easycart_paid_orders?: number;
  journey_purchase_sessions?: number;
  easycart_purchase_gap?: number;
  /** Journey ma więcej purchase niż EasyCart SSOT (w lejkach ucięte do EasyCart). */
  journey_purchase_overcount?: number;
  by_traffic: JourneyCompareFunnel[];
  by_visitor: JourneyCompareFunnel[];
  by_entry: JourneyCompareFunnel[];
  by_version: JourneyCompareFunnel[];
  by_via?: JourneyCompareFunnel[];
  exit_mix: JourneyExitMixRow[];
}

export interface SiteAnalyticsJourney {
  generated_at: string;
  window_days: number;
  ok: boolean;
  error?: string | null;
  period?: {
    days?: number;
    since?: string;
    until?: string | null;
    key?: string;
    timezone?: string;
    date_from?: string;
    date_to?: string;
    label_pl?: string;
    sessions?: number;
  };
  filters?: {
    entry?: string | null;
    visitor?: string | null;
    traffic?: string | null;
    version?: string | null;
    page_id?: string | null;
    via?: string | null;
    period?: string | null;
  };
  available_filters?: {
    entries?: string[];
    entry_labels?: Record<string, string>;
    visitors?: string[];
    traffics?: string[];
    traffic_labels?: Record<string, string>;
    versions?: string[];
    pages?: string[];
    page_labels?: Record<string, string>;
    vias?: string[];
  };
  conversion_matrix?: {
    cells: JourneyConversionCell[];
    min_sessions_default?: number;
  };
  version_compare?: {
    rows: JourneyVersionRow[];
  };
  page_versions?: JourneyPageVersions;
  cohorts?: {
    profiles: JourneyCohortProfile[];
    buy_vs_no_buy?: {
      purchased: JourneyBuyCompareSide;
      no_purchase: JourneyBuyCompareSide;
    };
    impulse_card?: JourneyImpulseCard | null;
  };
  content_reach?: {
    sections: JourneySectionReach[];
    cta?: JourneyCtaReach[];
  };
  session_funnel?: {
    steps: JourneyFunnelStep[];
  };
  funnels_compare?: JourneyFunnelsCompare;
  sections_by_page?: JourneySectionsByPage;
  product_funnels?: JourneyProductFunnels;
  easycart_align?: JourneyEasycartAlign;
  shopping_paths?: JourneyShoppingPaths;
  daily?: JourneyDailySeries;
  acquisition_channels?: JourneyAcquisitionChannels;
  total_sessions?: number;
}

export interface JourneyAcquisitionChannelRow {
  channel: string;
  label: string;
  sessions: number;
  share?: number;
}

export interface JourneyAcquisitionEntryGroup {
  label: string;
  total_sessions: number;
  new_sessions: number;
  all_visits?: JourneyAcquisitionChannelRow[];
  new_visits?: JourneyAcquisitionChannelRow[];
}

export interface JourneyAcquisitionChannels {
  note_pl?: string;
  all_visits?: JourneyAcquisitionChannelRow[];
  new_visits?: JourneyAcquisitionChannelRow[];
  total_sessions?: number;
  new_sessions?: number;
  entry_groups?: {
    paid?: JourneyAcquisitionEntryGroup;
    other?: JourneyAcquisitionEntryGroup;
  };
}

export interface JourneyDailyPoint {
  date: string;
  sessions?: number;
  cart_sessions?: number;
  purchase_sessions?: number;
  abandoned_sessions?: number;
  cart_to_purchase_rate?: number;
  easycart_paid_orders?: number;
  easycart_revenue?: number;
}

export interface JourneyDailySeries {
  timezone?: string;
  date_from?: string;
  date_to?: string;
  days?: JourneyDailyPoint[];
  note?: string;
}

export interface DayTrafficHourlyPoint {
  hour: number;
  label: string;
  sessions?: number;
  cart_sessions?: number;
  purchase_sessions?: number;
  paid_sessions?: number;
  other_sessions?: number;
  easycart_paid_orders?: number;
  easycart_revenue?: number;
  [key: string]: string | number | undefined;
}

export interface DayTrafficResponse {
  ok: boolean;
  period?: {
    key?: string;
    label_pl?: string;
    date?: string;
    timezone?: string;
  };
  summary?: {
    sessions: number;
    cart_sessions: number;
    purchase_sessions: number;
    easycart_paid_orders: number;
    easycart_revenue: number;
    paid_entries: number;
    other_entries: number;
    cart_to_purchase_rate: number;
  };
  hourly?: {
    timezone?: string;
    date?: string;
    hours?: DayTrafficHourlyPoint[];
  };
  peaks?: {
    visits_hour?: number | null;
    cart_hour?: number | null;
    revenue_hour?: number | null;
  };
  insights?: string[];
  error?: string;
}

export interface ContentTimelineGainReel {
  platform?: string;
  reel_key?: string;
  gain?: number;
  view_count?: number;
  url?: string;
  title?: string;
}

export interface ContentTimelineHourlyPoint {
  hour_key: string;
  label: string;
  published_count?: number;
  reach_total?: number;
  reach_gained?: number;
  reach_gained_ig?: number;
  reach_gained_fb?: number;
  reach_gained_carousel?: number;
  top_instagram?: ContentTimelineGainReel[];
  top_facebook?: ContentTimelineGainReel[];
  top_carousel?: ContentTimelineGainReel[];
  keyword_triggers?: number;
  site_sessions?: number;
  cart_sessions?: number;
  easycart_paid_orders?: number;
  easycart_revenue?: number;
}

export interface ContentTimelinePublication {
  platform: string;
  published_at: string;
  hour_key: string;
  url: string;
  title: string;
  view_count: number | null;
  keywords_total: number;
  time_precision: "hour" | "day";
}

export interface ContentTimelineFetchPlatform {
  platform?: string;
  backend?: string;
  status?: string;
  media?: number;
  comments?: number;
  items_in_dump?: number;
  error?: string;
}

export interface ContentTimelineFetchDump {
  exists?: boolean;
  items?: number;
  last_updated?: string;
  last_updated_pl?: string;
}

export interface ContentTimelineFetch {
  last_run_at?: string;
  last_run_at_pl?: string;
  trigger?: string;
  since?: string;
  days?: number | null;
  status?: string;
  platforms?: ContentTimelineFetchPlatform[];
  dumps?: Record<string, ContentTimelineFetchDump>;
}

export interface ContentTimelineResponse {
  ok: boolean;
  period?: {
    days?: number;
    date_from?: string;
    date_to?: string;
    timezone?: string;
    label_pl?: string;
  };
  summary?: {
    publications: number;
    reach_total: number;
    reach_gained?: number;
    reach_gained_ig?: number;
    reach_gained_fb?: number;
    reach_gained_carousel?: number;
    reach_missing: number;
    keyword_triggers: number;
    site_sessions?: number;
    cart_sessions?: number;
    easycart_paid_orders: number;
    easycart_revenue: number;
  };
  hourly?: {
    timezone?: string;
    hours?: ContentTimelineHourlyPoint[];
    gain_hours?: ContentTimelineHourlyPoint[];
  };
  publications?: ContentTimelinePublication[];
  notes_pl?: string[];
  fetch?: ContentTimelineFetch;
  error?: string;
}

export interface JourneyEasycartOrderRow {
  order_ref?: string;
  product_name?: string;
  slug?: string | null;
  revenue?: number;
  payment_status?: string;
  ordered_at?: string;
  ordered_at_pl?: string;
  in_kpi?: boolean;
}

export interface JourneyEasycartAlign {
  since?: string;
  until?: string | null;
  days?: number;
  timezone?: string;
  journey?: {
    cart_sessions?: number;
    purchase_sessions?: number;
    cart_to_purchase_rate?: number;
  };
  easycart?: {
    ok?: boolean;
    error?: string | null;
    sales?: number;
    paid_orders?: number;
    cancelled_orders?: number;
    customers?: number;
    revenue?: number;
    aov?: number;
    shopping_sessions?: number | null;
    shopping_sessions_note?: string;
    orders?: JourneyEasycartOrderRow[];
  };
  coverage?: {
    purchase_vs_easycart?: number | null;
    note?: string;
  };
}

export interface JourneyShoppingPathRow {
  path: string;
  sessions: number;
  share: number;
  purchase_rate?: number | null;
}

export interface JourneyShoppingPathMix {
  key: string;
  sessions: number;
  share: number;
}

export interface JourneyShoppingPaths {
  sessions: number;
  purchased: number;
  abandoned: number;
  purchase_rate: number;
  top_page_paths: JourneyShoppingPathRow[];
  top_section_paths: JourneyShoppingPathRow[];
  purchased_page_paths?: JourneyShoppingPathRow[];
  abandoned_page_paths?: JourneyShoppingPathRow[];
  entry_mix?: JourneyShoppingPathMix[];
  traffic_mix?: JourneyShoppingPathMix[];
  product_mix?: JourneyShoppingPathMix[];
  note?: string;
}

export type ShoppingSessionOutcomeFilter = "all" | "purchased" | "abandoned";

export interface ShoppingSessionsParams {
  period?: string;
  days?: number;
  outcome?: ShoppingSessionOutcomeFilter;
  limit?: number;
  offset?: number;
}

export interface ShoppingSessionRow {
  kz_jid: string;
  kz_jid_short: string;
  started_at: string;
  started_at_pl: string;
  outcome: "Zakup" | "Porzucenie";
  profile: string;
  profile_label: string;
  traffic_label: string;
  entry_label: string;
  via?: string | null;
  product_label: string;
  revenue?: number | null;
  order_ref?: string | null;
  ms_to_checkout?: number | null;
  active_ms?: number | null;
  max_scroll?: number | null;
  path_summary: string;
  attribution: string;
}

export interface ShoppingSessionInsight {
  text: string;
  severity: "info" | "warning";
}

export interface ShoppingSessionsResponse {
  ok: boolean;
  period?: SiteAnalyticsJourney["period"];
  summary: {
    cart_sessions: number;
    purchased: number;
    abandoned: number;
    cart_to_purchase_rate: number;
    paid_entries?: number;
    other_entries?: number;
  };
  trends: JourneyDailySeries & {
    delta?: {
      cart_sessions_delta_pct?: number | null;
      purchase_sessions_delta_pct?: number | null;
      prev_label_pl?: string;
    };
  };
  insights: ShoppingSessionInsight[];
  sessions: ShoppingSessionRow[];
  pagination: { total: number; limit: number; offset: number };
}

export interface ShoppingSessionTimelineItem {
  ts: string;
  ts_pl: string;
  event_name: string;
  label_pl: string;
  page_id?: string | null;
  section_id?: string | null;
  ms_since_session_start?: number | null;
}

export interface ShoppingSessionOrder {
  order_ref: string;
  product_name: string;
  product_label: string;
  revenue: number;
  payment_status: string;
  ordered_at: string;
  ordered_at_pl: string;
}

export interface ShoppingSessionDetailResponse {
  ok: boolean;
  error?: string;
  session?: ShoppingSessionRow;
  timeline?: ShoppingSessionTimelineItem[];
  order?: ShoppingSessionOrder | null;
  paths?: { page_path: string; section_path: string };
  quality?: { path_complete: boolean; weak_attribution: boolean };
}

export interface JourneyProductFunnels {
  steps: string[];
  min_sessions?: number;
  products: JourneyCompareFunnel[];
  purchase_ssot?: string;
}

export interface JourneyPageSectionCta {
  label: string;
  intent: string;
  clicks: number;
}

export interface JourneyPageSectionRow {
  section_id: string;
  order: number;
  sessions_reached: number;
  view_rate: number;
  view_rate_purchased?: number;
  cta_clicks?: number;
  cta_sessions?: number;
  cta_rate?: number;
  cta_rate_of_viewers?: number;
  ctas?: JourneyPageSectionCta[];
}

export interface JourneyPageSectionsColumn {
  page_id: string;
  label: string;
  sessions: number;
  sections: JourneyPageSectionRow[];
}

export interface JourneySectionsByPage {
  pages: JourneyPageSectionsColumn[];
}

export interface LandingPreviewPage {
  page_id: string;
  label: string;
  live_url: string;
}

export interface LandingPreviewCatalogResponse {
  ok: boolean;
  version: number;
  pages: LandingPreviewPage[];
  section_labels: Record<string, string>;
  page_count: number;
}

export interface LandingPreviewStructureResponse {
  ok: boolean;
  page_id: string;
  live_url: string;
  section_ids: string[];
  fetched_at?: string | null;
  error?: string | null;
}

export interface Video {
  date: string;
  platform: string;
  title: string;
  views: number;
  ctr: number | null;
  comments_count: number;
  url: string | null;
}

export interface CommentsFilterParams {
  q?: string;
  platform?: string;
  window?: string;
  month?: string;
  class?: string;
  pain?: string;
  cta?: boolean;
  is_reply?: boolean;
  post_id?: string;
  action?: boolean;
}

export interface CommentsListParams extends CommentsFilterParams {
  bucket?: string;
  limit?: number;
  offset?: number;
}

export interface Comment {
  id: string;
  platform: string;
  platform_id: string;
  video_title: string;
  author: string;
  text: string;
  created_at: string;
  weight: number;
  bucket: string;
  answered: boolean;
  story_class: string;
  class_label: string;
  cta: boolean;
  pain_ids: string[];
  score: number;
  intent_label: string;
  score_reasons: string[];
  action: boolean;
  is_reply: boolean;
  is_owner: boolean;
  like_count: number;
  post_id: string;
  post_url: string | null;
  month: string;
}

export interface CommentListResponse {
  items: Comment[];
  total: number;
  offset: number;
  limit: number;
}

export interface SocialProofListParams {
  q?: string;
  proof_type?: string;
  status?: string;
  source_kind?: string;
  limit?: number;
  offset?: number;
}

export interface SocialProofItem {
  id: string;
  source_kind: string;
  source_id: string;
  proof_type: string;
  proof_label: string;
  score: number;
  score_reasons: string[];
  text: string;
  quote_anon: string;
  author: string;
  author_id: string;
  username: string;
  display_handle: string;
  avatar_url?: string | null;
  platform: string;
  created_at: string;
  post_url: string;
  inbox_link: string;
  story_class: string;
  class_label: string;
  like_count: number;
  status: string;
  used_on: string[];
  suitable_for_screenshot: boolean;
  notes: string;
  found_at: string;
}

export interface SocialProofListResponse {
  items: SocialProofItem[];
  total: number;
  offset: number;
  limit: number;
  meta?: Record<string, unknown>;
  counts?: {
    by_type?: Record<string, number>;
    by_status?: Record<string, number>;
    by_source?: Record<string, number>;
  };
}

export interface SocialProofFacets {
  proof_types: { id: string; label: string }[];
  statuses: string[];
  source_kinds: string[];
  counts?: SocialProofListResponse["counts"];
  meta?: Record<string, unknown>;
}

export interface SocialProofFreshness {
  inbox?: {
    messenger?: { threads?: number; fetched_at?: string; messages?: number };
    instagram?: { threads?: number; fetched_at?: string; messages?: number };
    ig_dm_alert?: string | null;
    ops_index?: { exists?: boolean; mtime?: string };
  };
  bank_meta?: {
    last_rescan_at?: string;
    counts?: SocialProofListResponse["counts"];
  };
  bank_exists?: boolean;
}

export interface SocialProofPatchBody {
  status?: string;
  quote_anon?: string;
  notes?: string;
  suitable_for_screenshot?: boolean;
  used_on?: string[];
}

export interface SocialProofExportResponse {
  exported_at: string;
  count: number;
  items: Array<{
    id: string;
    proof_type: string;
    quote: string;
    platform: string;
    source_kind: string;
    display_name: string;
    used_on: string[];
  }>;
  note?: string;
}

export interface CommentsStatsResponse {
  payload: {
    total: number;
    total_all: number;
    last_7d: number;
    cta: number;
    cta_pct: number;
    historia: number;
    pytanie: number;
    action: number;
    classes: { id: string; label: string; count: number }[];
    platforms: { id: string; count: number }[];
    pains: { id: string; label: string; count: number }[];
    daily: Array<{ date: string; youtube?: number; instagram?: number; tiktok?: number; facebook?: number; total?: number }>;
  };
}

export type BrandBarometerLabel = "spokoj" | "napiecie" | "alarm" | "malo_danych";

export interface BrandBarometerResponse {
  payload: {
    window: string;
    score: {
      value: number | null;
      label: BrandBarometerLabel;
      components: {
        tone: number | null;
        buffer: number | null;
        coverage: number | null;
        commerce: number | null;
      };
      weights: {
        tone: number;
        buffer: number;
        coverage: number;
        commerce: number;
      };
    };
    classes: { id: string; label: string; count: number }[];
    pains: { id: string; label: string; count: number }[];
    tokens: { token: string; count: number }[];
    daily: Array<{
      date: string;
      youtube?: number;
      instagram?: number;
      tiktok?: number;
      facebook?: number;
      total?: number;
    }>;
    volume: {
      total: number;
      total_all: number;
      last_7d: number;
      cta: number;
      cta_pct: number;
      action: number;
      historia: number;
      pytanie: number;
    };
    emotional: {
      hard_questions: {
        platform?: string;
        author?: string;
        post_id?: string;
        text?: string;
      }[];
      supportive: string[];
      quarantined: string[];
      supportive_count: number;
      quarantined_count: number;
    };
    coverage: {
      owner_reply_pct: number;
      owner_reply_posts: number;
      total_posts: number;
      items: {
        platform: string;
        days_with_comments: number;
        missing_days: number;
        comments_in_range: number;
        source_last_updated?: string;
      }[];
      quality_flags: string[];
      recommendations: string[];
      date_from?: string;
      date_to?: string;
      recent_days: number;
    };
    freshness: {
      live_total?: number;
      platforms?: {
        id: string;
        status: string;
        age_hours?: number | null;
      }[];
      unified?: {
        generated_at?: string;
        total_comments?: number;
      };
    };
    funnel: {
      cta_pct: number;
      cta: number;
      action: number;
      sales: {
        ok: boolean;
        days?: number;
        error?: string;
        cards: {
          id?: string;
          label?: string;
          value_display?: string;
          compare_text?: string;
          status?: string;
        }[];
        verdicts: { id?: string; text?: string; status?: string }[];
      };
      reach_7d: {
        ok: boolean;
        error?: string;
        reach_gained: number;
        reach_gained_ig: number;
        reach_gained_fb: number;
        reach_gained_carousel: number;
        keyword_triggers: number;
        publications: number;
        note?: string;
      };
    };
  };
}

export interface CommentsFacetsResponse {
  payload: {
    total: number;
    platforms: Record<string, number>;
    classes: Record<string, { count: number; label: string }>;
    months: Record<string, number>;
    pains: Record<string, { count: number; label: string }>;
    cta: number;
    action: number;
    historia: number;
    pytanie: number;
    atak_forma: number;
    replies: number;
  };
}

export interface CommentsPostRow {
  platform: string;
  post_id: string;
  title: string;
  url: string | null;
  comments: number;
  historia: number;
  cta: number;
  action: number;
  owner_reply: boolean;
  latest_at: string;
}

export interface CommentsPostsResponse {
  payload: {
    items: CommentsPostRow[];
    total_posts: number;
    owner_reply_posts: number;
    owner_reply_pct: number;
  };
}

export interface CommentsFreshnessResponse {
  payload: {
    live_total: number;
    platforms: {
      id: string;
      exists: boolean;
      last_updated?: string | null;
      age_hours?: number | null;
      status: string;
    }[];
    unified: { generated_at?: string; total_comments?: number; by_platform?: Record<string, number> };
  };
}

export interface CommentsCoverageResponse {
  payload: {
    items: {
      platform: string;
      days_with_comments: number;
      missing_days: number;
      comments_in_range: number;
      source_last_updated?: string | null;
    }[];
    quality_flags: string[];
    recommendations: string[];
    owner_reply_pct: number;
    owner_reply_posts: number;
    total_posts: number;
    date_from?: string;
    date_to?: string;
  };
}

export interface CommentsDigestResponse {
  payload: {
    status?: string;
    digest?: {
      generated_at?: string;
      windows?: Record<string, { top_comments?: Array<{ content?: string; platform?: string; business_score?: number }> }>;
    };
    message?: string;
  };
}

export interface CommentsRefreshResponse {
  status: string;
  snapshot?: { total_comments?: number; by_platform?: Record<string, number>; refreshed_at?: string };
  digest?: { status?: string; generated_at?: string };
  live_total?: number;
}

export interface Coverage {
  video_title: string;
  total: number;
  answered: number;
  pct: number;
}

export interface AgentRole {
  id: string;
  label: string;
  axis: string;
  source: string;
  forbidden: string;
}

export interface NaradaSchema {
  id: string;
  label: string;
  plain?: string;
  axis: string;
  default_area_ids: string[];
  parking: boolean;
}

export interface NaradaTheme {
  id: string;
  label: string;
  plain: string;
  schema_ids: string[];
  default_schema_id: string;
}

export interface NaradaArea {
  id: string;
  label: string;
  group: string;
}

export interface NaradaGroup {
  id: string;
  label: string;
  areas: NaradaArea[];
}

export interface NaradaCatalog {
  schemas: NaradaSchema[];
  groups: NaradaGroup[];
  themes?: NaradaTheme[];
}

export interface NaradaSuggestion {
  title: string;
  why: string;
  owner: string;
  handoff: string;
}

export interface Discussion {
  id: number;
  timestamp: string;
  status: string | null;
  schema_id?: string | null;
  schema_label?: string | null;
  question?: string | null;
  verdict_preview?: string | null;
}

export interface DiscussionDetail extends Discussion {
  area_ids: string[];
  panel: { id: string; label: string; text: string }[];
  verdict?: string | null;
  suggestions: NaradaSuggestion[];
  profile_tilt?: string | null;
  error?: string | null;
}

export interface FulfillmentSummary {
  to_ship: number;
  new: number;
  ready_to_reship?: number;
  shipment_created: number;
  dedication: number;
  packing: number;
  ready_to_ship: number;
  /** @deprecated użyj dedication */
  preparing: number;
  shipped_today: number;
  with_dedication: number;
  complaints: number;
  active_orders: number;
  /** Suma sztuk książek w aktywnym pipeline (zrealizowane, nie anulowane). */
  total_books?: number;
  by_status: Record<string, number>;
  workflow_labels?: Record<string, string>;
}

export interface FulfillmentImportResult {
  status: string;
  message?: string;
  path?: string;
  inserted?: number;
  updated?: number;
  skipped?: number;
  csv_row_count?: number;
  csv_mtime_iso?: string;
  finished_at?: string;
  webhook?: FulfillmentImportResult;
  csv?: FulfillmentImportResult;
}

export interface FulfillmentImportStatus {
  last_import: FulfillmentImportResult & Record<string, unknown>;
  csv: {
    path: string;
    exists: boolean;
    valid_format?: boolean;
    mtime_iso?: string;
    row_count?: number;
  };
  db: {
    total: number;
    visible_in_list: number;
    max_ordered_at: string | null;
  };
  gaps?: {
    zrealizowane_missing_in_db: number;
    sample_missing_ids?: string[];
    csv_newest_order_id?: string | null;
    csv_newest_ordered_at?: string | null;
    hidden_non_zrealizowane_in_db?: number;
  };
  webhook_events_total?: number;
}

export type FulfillmentOrderPatch = Partial<
  Pick<
    FulfillmentOrder,
    | "fulfillment_status"
    | "internal_notes"
    | "furgonetka_tracking"
    | "customer_name"
    | "customer_email"
    | "phone"
    | "shipping_name"
    | "shipping_street"
    | "shipping_postal_code"
    | "shipping_city"
    | "dedication"
    | "paczkomat_name"
  >
>;

export type FulfillmentAdditionalShipmentRequest = {
  note?: string;
} & Partial<
  Pick<
    FulfillmentOrder,
    | "customer_name"
    | "customer_email"
    | "phone"
    | "shipping_name"
    | "shipping_street"
    | "shipping_postal_code"
    | "shipping_city"
    | "dedication"
    | "paczkomat_name"
    | "internal_notes"
  >
>;

export interface FulfillmentAdditionalShipmentResult {
  status: string;
  message?: string;
  order?: FulfillmentOrderDetail;
  archived_package?: {
    previous_package_id?: string;
    previous_tracking?: string | null;
    previous_status_label?: string | null;
    previous_fulfillment_status?: string | null;
    note?: string | null;
  };
}

export interface PaczkomatDeliveryAddress {
  point_code: string | null;
  point_name: string | null;
  street: string | null;
  city: string | null;
  postcode: string | null;
  resolved: boolean;
  corrected_from?: string | null;
  service?: string | null;
  candidates?: PaczkomatResolveCandidate[];
  search_error?: string | null;
  label?: string | null;
}

export interface PaczkomatResolveCandidate {
  code: string;
  service: string;
  score: number;
  street?: string | null;
  city?: string | null;
  postcode?: string | null;
  label?: string | null;
}

export interface PaczkomatResolveRequest {
  apply?: boolean;
  point_code?: string;
  service?: string;
}

export interface PaczkomatResolveResponse {
  resolved: boolean;
  confidence: string;
  service?: string | null;
  point_code?: string | null;
  label?: string | null;
  candidates: PaczkomatResolveCandidate[];
  applied: boolean;
  error?: string | null;
  paczkomat_delivery_address?: PaczkomatDeliveryAddress | null;
  order?: FulfillmentOrderDetail;
}

export interface FulfillmentOrder {
  id: number;
  easycart_order_id: string;
  customer_name: string | null;
  customer_email: string | null;
  phone: string | null;
  shipping_name?: string | null;
  shipping_street: string | null;
  shipping_postal_code: string | null;
  shipping_city: string | null;
  dedication: string | null;
  book_quantity: number;
  customer_books_total?: number;
  amount?: number | null;
  currency?: string | null;
  paczkomat_name: string | null;
  paczkomat_courier?: string | null;
  paczkomat_courier_label?: string | null;
  paczkomat_courier_confidence?: string | null;
  shipping_is_foreign?: boolean;
  paczkomat_delivery_address?: PaczkomatDeliveryAddress | null;
  paczkomat_match_pending?: boolean;
  paczkomat_match_status?: PaczkomatMatchStatus;
  paczkomat_match_label?: string;
  paczkomat_match_error?: string;
  paczkomat_match_point_code?: string;
  paczkomat_match_at?: string | null;
  fulfillment_status: string;
  internal_notes?: string | null;
  furgonetka_package_id: string | null;
  furgonetka_tracking: string | null;
  furgonetka_price_pln?: number | null;
  furgonetka_expected: boolean;
  furgonetka_last_error: string | null;
  furgonetka_package_state?: string | null;
  furgonetka_paid?: boolean;
  furgonetka_dispatched?: boolean;
  furgonetka_delivered?: boolean;
  furgonetka_returned?: boolean;
  furgonetka_undelivered?: boolean;
  shipment_attempt?: number;
  furgonetka_status_label?: string | null;
  furgonetka_paid_at?: string | null;
  furgonetka_status_at?: string | null;
  furgonetka_status_synced_at?: string | null;
  ordered_at: string | null;
  updated_at?: string | null;
}

export type PaczkomatMatchStatus =
  | "running"
  | "pending"
  | "success"
  | "failed"
  | "outside_queue";

export interface PaczkomatBackgroundMeta {
  max_orders: number;
  queue_size: number;
  running_order_id: number | null;
  running_easycart_order_id: string | null;
}

export interface FulfillmentOrderDetail extends FulfillmentOrder {
  shipping_raw: string | null;
  events: { id: number; event_type: string; note: string | null; created_at: string }[];
}

export interface FulfillmentListResponse {
  items: FulfillmentOrder[];
  total: number;
  limit?: number;
  offset?: number;
  paczkomat_background?: PaczkomatBackgroundMeta;
}

export interface FurgonetkaDropPoint {
  code?: string;
  name?: string;
  label?: string;
  street?: string;
  city?: string;
  postcode?: string;
}

export interface FurgonetkaConfig {
  configured: boolean;
  sandbox_mode: boolean;
  api_base: string;
  auth_mode: string;
  user_auth_configured: boolean;
  sandbox_credentials_separate?: boolean | null;
  default_service: string;
  selected_courier?: FurgonetkaCourier | null;
  shipment_mode?: string;
  shipment_modes?: FurgonetkaShipmentMode[];
  parcel?: FurgonetkaParcelSettings;
  drop_point?: FurgonetkaDropPoint | null;
  inpost_drop_point?: FurgonetkaDropPoint | null;
  inpost_drop_any_apm?: boolean;
}

export interface FurgonetkaParcelSettings {
  parcel_type: "dox" | "package" | "palette";
  length_cm: number;
  width_cm: number;
  height_cm: number;
  weight_kg: number;
  value_pln: number;
  content: string;
}

export interface FurgonetkaShipmentMode {
  id: string;
  label: string;
  shipment_type: string;
  description?: string;
}

export interface FurgonetkaCourier {
  service_id: number;
  service: string;
  label?: string;
  price_pln?: number | null;
  insurance_label?: string | null;
  delivery_time_label?: string | null;
  unavailable_reason?: string | null;
  available_for_mode?: boolean;
  pickup_from_address?: boolean;
}

export interface FurgonetkaServicesResponse {
  items: FurgonetkaCourier[];
  selected?: FurgonetkaCourier | null;
  shipment_mode?: string;
  shipment_modes?: FurgonetkaShipmentMode[];
  message?: string;
}

export interface FurgonetkaBatchResult {
  status: string;
  total_price_pln?: number;
  results: {
    status: string;
    order_id?: number;
    message?: string;
    package_id?: string;
    price_pln?: number;
    furgonetka_edit_url?: string;
    pickup_manual_fix_required?: boolean;
  }[];
}

export interface FurgonetkaSyncStatusResult {
  status: string;
  synced?: number;
  paid?: number;
  dispatched?: number;
  results: {
    status: string;
    order_id?: number;
    easycart_order_id?: string;
    message?: string;
    tracking?: string;
    furgonetka_paid?: boolean;
    furgonetka_dispatched?: boolean;
    furgonetka_delivered?: boolean;
    furgonetka_package_state?: string;
    furgonetka_status_label?: string;
  }[];
}

export interface MessengerStoriesParams {
  q?: string;
  class?: string;
  theme?: string;
  persona?: string;
  b_signal?: string;
  month?: string;
  length_band?: string;
  has_purchase?: boolean;
  limit?: number;
  offset?: number;
}

export interface MessengerStory {
  story_id: string;
  date: string;
  month: string;
  length: number;
  class_: string;
  theme: string;
  summary?: string;
  full_text?: string;
  tags?: string[];
  b_signals?: string[];
  personas?: string[];
  length_band?: string;
  participant_id?: string;
  participant_name?: string;
  inbox_link?: string;
  thread_id?: string;
  message_id?: string;
  created_at?: string;
  emails_in_thread?: string[];
}

export interface MessengerStoryListResponse {
  items: MessengerStory[];
  total: number;
  offset: number;
  limit: number;
  index_exists: boolean;
}

export interface MessengerStoriesFacets {
  payload?: {
    classes?: Record<string, number>;
    themes?: Record<string, number>;
    personas?: Record<string, number>;
    b_signals?: Record<string, number>;
    months?: Record<string, number>;
    length_bands?: Record<string, number>;
    total?: number;
    index_exists?: boolean;
  };
}

export interface MessengerThreadMessage {
  id: string;
  created_at: string;
  text: string;
  from_id: string;
  from_name: string;
  sender: "page" | "user";
}

export interface MessengerThread {
  thread_id: string;
  updated_at: string;
  inbox_link: string;
  participant_id: string;
  participant_name: string;
  message_count: number;
  messages: MessengerThreadMessage[];
}

export interface MessengerProfileComment {
  id: string;
  platform: string;
  author: string;
  text: string;
  created_at: string;
  post_id: string;
  match_confidence: string;
  match_reason: string;
}

export interface MessengerProfileOrder {
  order_ref: string;
  product_name: string;
  customer_email: string;
  revenue_amount: number;
  currency: string;
  ordered_at: string;
  match_confidence: string;
  fulfillment_status: string;
  fulfillment_label: string;
}

export interface MessengerParticipantProfile {
  participant_id: string;
  participant_name: string;
  inbox_link: string;
  emails: string[];
  messenger_threads: MessengerThread[];
  comments: MessengerProfileComment[];
  orders: MessengerProfileOrder[];
  person_entities: {
    person_id: string;
    platform: string;
    author_display: string;
    comment_count: number;
    match_confidence: string;
  }[];
  order_links: { order_ref: string; confidence: string }[];
  journey_note: string;
}

export interface BartSource {
  source: string;
  layer: string;
  score: number;
}

export interface BartThread {
  id: string;
  title: string;
  channel: string;
  telegram_chat_id?: string | null;
  telegram_account?: string;
  telegram_handle?: string;
  topic?: string;
  created_at: string;
  updated_at: string;
  pinned?: boolean;
  pinned_at?: string | null;
}

export interface BartUpload {
  id: string;
  kind: string;
  title?: string;
  mime?: string;
  url: string;
}

export interface BartAttachment {
  kind: "image" | "link" | string;
  id?: string;
  url?: string;
  title?: string;
  label?: string;
  source?: string;
}

export interface BartMessage {
  id: string;
  thread_id: string;
  role: "user" | "assistant" | string;
  content: string;
  sources?: BartSource[];
  attachments?: BartAttachment[];
  created_at: string;
}

export interface BartThreadsResponse {
  items: BartThread[];
}

export interface BartThreadDetail {
  thread: BartThread;
  messages: BartMessage[];
}

export interface BartChatResponse {
  thread: BartThread;
  messages: BartMessage[];
  answer: string;
  thought?: string;
  sources: BartSource[];
  attachments?: BartAttachment[];
}

export interface BartNudgeResponse extends BartChatResponse {
  ok: boolean;
  skipped: boolean;
  reason?: string;
  retry_after_sec?: number;
}

export interface BartNote {
  id: string;
  kind: string;
  text: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface BartNotesResponse {
  items: BartNote[];
}
